// OrderDetailPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  InputAdornment,
  FormControl,
  FormHelperText,
  Select,
  OutlinedInput,
  ButtonGroup,
  Collapse,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import AddProductsToOrderDialog from './components/order/AddProductsToOrderDialog';
import InventoryManagementDialog from './dialogs/InventoryManagementDialog';
import MobileDialog from './dialogs/MobileDialog';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';
import { generateBarcodeSVGString } from '../utils/barcodePrintUtils';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/x-data-grid/locales';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { format } from 'date-fns';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import InputLabel from '@mui/material/InputLabel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PrintIcon from '@mui/icons-material/Print';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SearchIcon from '@mui/icons-material/Search';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
});

// Fetch order function
const fetchOrder = async (orderId) => {
  const response = await API.get(`/order/?OrderID=${orderId}`);
  return response.data;
};

// Fetch products function
const fetchProducts = async () => {
  try {
    const response = await API.get('/products/');
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

const OrderDetailPage = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};
  const role = userProfile?.role;
  
  // State for dialogs
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentAmountError, setPaymentAmountError] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkError, setCheckError] = useState('');
  const [checkDate, setCheckDate] = useState(null);
  const [checkBankName, setCheckBankName] = useState('');
  const [checkAccountNumber, setCheckAccountNumber] = useState('');
  const [cardLastFour, setCardLastFour] = useState('');
  const [cardType, setCardType] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccountLast4, setBankAccountLast4] = useState('');
  const [transferDate, setTransferDate] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [refundPayments, setRefundPayments] = useState(true);
  const [cancelNotes, setCancelNotes] = useState('');
  
  // State for due date edit
  const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
  const [newDueDate, setNewDueDate] = useState(null);
  
  // NEW: State for order date edit
  const [orderDateDialogOpen, setOrderDateDialogOpen] = useState(false);
  const [newOrderDate, setNewOrderDate] = useState(null);
  
  // State for payment edit/delete
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletePaymentId, setDeletePaymentId] = useState(null);

  // Notes state functionality
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');

  // Enhanced notes state
  const [expandedNotesDialog, setExpandedNotesDialog] = useState(false);
  const [notesLoadingState, setNotesLoadingState] = useState(false);
  const [notesErrorMsg, setNotesErrorMsg] = useState('');
  const [notesSuccessMsg, setNotesSuccessMsg] = useState('');
  const notesTextFieldRef = useRef(null);
  const isFinishingProductsRef = useRef(false);
  const isAddingProductRef = useRef(false);

  // Refs for product dialog inputs
  const productSearchInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const unitPriceInputRef = useRef(null);
  const addProductButtonRef = useRef(null);

  // Rounding state
  const [roundingEnabled, setRoundingEnabled] = useState(false);
  
  // Product editing state
  const [productEditDialogOpen, setProductEditDialogOpen] = useState(false);
  const [editingProductItem, setEditingProductItem] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [productUnitPrice, setProductUnitPrice] = useState('');
  const [productDialogMode, setProductDialogMode] = useState('edit'); // 'edit' or 'add'
  
  // Product selection state
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [productAutocompleteOpen, setProductAutocompleteOpen] = useState(false);

  // Product creation state
  const [productCreationMode, setProductCreationMode] = useState(false);
  const [addedProducts, setAddedProducts] = useState([]);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isFinishingProducts, setIsFinishingProducts] = useState(false);
  const [createProductError, setCreateProductError] = useState('');
  const [createProductSuccess, setCreateProductSuccess] = useState('');

  // state for order item status 
  const [itemStatusDialogOpen, setItemStatusDialogOpen] = useState(false);
  const [editingItemStatus, setEditingItemStatus] = useState(null);
  const [newItemStatus, setNewItemStatus] = useState('');

  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const orderFields = uiConfig.order_fields || [];
  const orderFieldsSchema = uiConfig.order_fields_schema || {};
  const orderConfig =  userProfile?.group?.order_config || {};
  const orderItemFields = uiConfig.order_item_fields || [];
  const orderItemFieldsSchema = uiConfig.order_item_fields_schema || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {}; 
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];
  // Extra per-item blocks to render inside the printable items table.
  // String list so groups can opt in to just what they want — e.g. ["Barcode"]
  // renders a scannable barcode SVG under the product name. Empty / missing
  // means the print stays compact even when barcodes are globally enabled.
  const orderPrintItemFields = uiConfig.pages_configs?.OrderDetailPage?.print_item_fields || [];
  // Per-group symbology preference for printed barcodes. The detection
  // pipeline always tries the strict numeric formats first (EAN13 / EAN8 /
  // ITF14). Groups that use CODE39 for their SKUs set this to "CODE39" so
  // ASCII-safe values render as CODE39 instead of the CODE128 fallback.
  // Default undefined → legacy auto-detect (CODE128 fallback for ASCII).
  const barcodePrintSymbology = userProfile?.group?.barcode_config?.print_symbology;

  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema?.order?.plural_label || 'הזמנות';
  const newOrderLabel = generalSchema?.new_order?.label || 'הזמנה חדשה';

  // Smart back navigation: remember where the user came from
  const backPath = location.state?.from || '/orders';
  const backToClient = typeof backPath === 'string' && backPath.startsWith('/client/');
  const backLabel = backToClient ? 'חזרה ללקוח' : `חזרה ל${orderPluralLabel}`;

  // 1. First, add storage-related imports and state variables (add after existing state declarations around line 117)
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [availableStorages, setAvailableStorages] = useState([]);
  const [editingProductInventory, setEditingProductInventory] = useState(null);
  const [noInventoryWarning, setNoInventoryWarning] = useState(false);
  // Inventory adjust (data-grid dialog) launched from the Edit Product flow.
  const [adjustInventoryOpen, setAdjustInventoryOpen] = useState(false);
  const [adjustInventoryProduct, setAdjustInventoryProduct] = useState(null);

  // Add storage configuration detection (add after uiConfig declarations around line 224)
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled || false;
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};

  // Reuse the ProductsPage inventory role keys — same config, same
  // defaults (both roles). If the group restricted take/add to managers
  // and an employee clicks עדכן מלאי in the order flow, we fall back to
  // a Hebrew prompt asking them to involve a manager instead of silently
  // hiding the button. UI-only gate; no backend enforcement.
  const productsPageConfig = uiConfig.pages_configs?.ProductsPage || {};
  const canTakeInventory = (productsPageConfig.inventory_take_roles || ['manager', 'employee']).includes(role);
  const canAddInventory = (productsPageConfig.inventory_add_roles || ['manager', 'employee']).includes(role);
  const canAdjustInventory = canTakeInventory || canAddInventory;

  const [newProductData, setNewProductData] = useState(() => {
    const initialData = {
      ProductName: '',
      Description: '',
      Price: 0,
      Quantity: ''
    };
    
    // Add all non-readonly and non-excluded fields from schema
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
    Object.entries(productFieldsSchema).forEach(([fieldName, fieldConfig]) => {
      if (!fieldConfig.readonly && 
          !initialData.hasOwnProperty(fieldName) && 
          productFields.includes(fieldName) &&
          !excludedFields.includes(fieldName)) {
        initialData[fieldName] = fieldConfig.default || '';
      }
    });
    
    return initialData;
  });

  const [newProductInventory, setNewProductInventory] = useState({});
  const [creatingProductWithInventory, setCreatingProductWithInventory] = useState(false);
  const [selectedStorageForNewProduct, setSelectedStorageForNewProduct] = useState(null);



  const {
    data: allStorages = [],
    isLoading: isLoadingStorages,
  } = useQuery({
    queryKey: ['storages'],
    queryFn: async () => {
      const response = await API.get('/storages/');
      return response.data;
    },
    enabled: storageEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // React Query
  const {
    data: order,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['order', order_id],
    queryFn: () => fetchOrder(order_id),
    enabled: !!order_id,
    staleTime: 1 * 60 * 1000,
    onSuccess: (data) => {
      // Initialize notes value from the fetched order
      setNotesValue(data.Notes || '');
      // Initialize due date when order is fetched
      setNewDueDate(data.DueDate ? new Date(data.DueDate) : null);
      // Initialize order date when order is fetched
      setNewOrderDate(data.OrderDate ? new Date(data.OrderDate) : null);
      // Initialize rounding state based on order data
      setRoundingEnabled(data.RoundingEnabled || false);
    },
  });

  // Update rounding state when order data changes
  useEffect(() => {
    if (order) {
      setRoundingEnabled(order.RoundingEnabled === true);
    }
  }, [order]);

  // Fetch products
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Update Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus) => {
      const response = await API.post('/order/status/', {
        OrderID: order_id,
        Status: newStatus
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('הסטטוס עודכן בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      setStatusDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון הסטטוס');
    },
  });
  
  // Update Due Date Mutation
  const updateDueDateMutation = useMutation({
    mutationFn: async (dueDate) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        DueDate: dueDate ? format(dueDate, 'yyyy-MM-dd') : null
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('תאריך אספקה עודכן בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      setDueDateDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון תאריך אספקה');
    },
  });
  
  // NEW: Update Order Date Mutation
  const updateOrderDateMutation = useMutation({
    mutationFn: async (orderDate) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        OrderDate: orderDate ? format(orderDate, 'yyyy-MM-dd') : null
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('תאריך הזמנה עודכן בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      setOrderDateDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון תאריך הזמנה');
    },
  });
  
  // Delete Order Mutation
  const deleteOrderMutation = useMutation({
    mutationFn: async () => {
      const response = await API.post('/order/delete/', {
        OrderID: order_id
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      navigate('/orders', { replace: true });
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || `שגיאה במחיקת ה${orderLabel}`);
    },
  });
  
  // Cancel Order Mutation
  const cancelOrderMutation = useMutation({
    mutationFn: async () => {
      const response = await API.post('/order/status/', {
        OrderID: order_id,
        Status: 'cancelled',
        Notes: cancelNotes,
        RefundPayments: refundPayments
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage(`ה${orderLabel} בוטלה בהצלחה`);
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      setCancelDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || `שגיאה בביטול ה${orderLabel}`);
    },
  });
  
  // Add Payment Mutation
  const addPaymentMutation = useMutation({
    mutationFn: async (paymentData) => {
      const response = await API.post('/order/payment/', paymentData);
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('התשלום נוסף בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      resetPaymentForm();
      setPaymentDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בהוספת תשלום');
    },
  });
  
  // Edit Payment Mutation
  const editPaymentMutation = useMutation({
    mutationFn: async (paymentData) => {
      const response = await API.put('/order/payment/edit/', paymentData);
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('התשלום עודכן בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      resetPaymentForm();
      setEditPaymentDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון תשלום');
    },
  });
  
  // Delete Payment Mutation
  const deletePaymentMutation = useMutation({
    mutationFn: async (paymentId) => {
      const response = await API.post('/order/payment/delete/', {
        PaymentID: paymentId
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('התשלום נמחק בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      queryClient.invalidateQueries(['orders']);
      setDeletePaymentDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה במחיקת תשלום');
    },
  });

  useEffect(() => {
    if (productCreationMode && storageEnabled && allStorages.length > 0) {
      // Initialize inventory with 0 for all storages
      const initialInventory = {};
      allStorages.forEach(storage => {
        initialInventory[storage.StorageID] = {
          quantity: 0,
          storageName: storage.StorageName,
          is_default: storage.is_default
        };
      });
      setNewProductInventory(initialInventory);
      
      // Pre-select default storage
      const defaultStorage = allStorages.find(s => s.is_default);
      setSelectedStorageForNewProduct(defaultStorage || allStorages[0]);
    }
  }, [productCreationMode, storageEnabled, allStorages]);

  useEffect(() => {
    if (order?.Notes !== undefined) {
      setNotesValue(order.Notes || '');
    }
  }, [order]);

  // Update Notes Mutation
  const updateNotesMutation = useMutation({
    mutationFn: async (notes) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        Notes: notes
      });
      return response.data;
    },
    onSuccess: () => {
      setNotesSuccessMsg('ההערות עודכנו בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      setEditingNotes(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setNotesSuccessMsg('');
      }, 3000);
    },
    onError: (error) => {
      setNotesErrorMsg(error?.response?.data?.error || 'שגיאה בעדכון ההערות');
    },
  });
  
  // Update Product Mutation
  const updateProductMutation = useMutation({
    mutationFn: async (productData) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        items_to_update: [productData]
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage(`ה${productLabel} עודכן בהצלחה`);
      queryClient.invalidateQueries(['order', order_id]);
      setProductEditDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || `שגיאה בעדכון ה${productLabel}`);
    },
  });
  
  // Add Product Mutation (single item)
  const addProductMutation = useMutation({
    mutationFn: async (productData) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        items_to_add: [productData]
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage(`ה${productLabel} נוסף בהצלחה`);
      queryClient.invalidateQueries(['order', order_id]);
      setAddProductDialogOpen(false);
      setAddedProducts([]);

      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || `שגיאה בהוספת ה${productLabel}`);
    },
  });

  // Batch Add/Update Products Mutation - handles multiple items in a single request
  // This prevents race conditions and creates a single transaction/debt update
  const batchProductsMutation = useMutation({
    mutationFn: async ({ itemsToAdd, itemsToUpdate }) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        items_to_add: itemsToAdd,
        items_to_update: itemsToUpdate
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage(`ה${productLabel}ים עודכנו בהצלחה`);
      queryClient.invalidateQueries(['order', order_id]);
      setAddProductDialogOpen(false);
      setProductAutocompleteOpen(false);
      setAddedProducts([]);
      setSelectedStorage(null);
      setAvailableStorages([]);
      isFinishingProductsRef.current = false;
      setIsFinishingProducts(false);

      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || `שגיאה בעדכון ה${productLabel}ים`);
      isFinishingProductsRef.current = false;
      setIsFinishingProducts(false);
    },
  });

  // Delete Product Mutation
  const deleteProductMutation = useMutation({
    mutationFn: async (productData) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        items_to_delete: [productData]
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage(`ה${productLabel} נמחק בהצלחה`);
      queryClient.invalidateQueries(['order', order_id]);
      setDeleteProductDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || `שגיאה במחיקת ה${productLabel}`);
    },
  });

  // Update Rounding Mutation
  const updateRoundingMutation = useMutation({
    mutationFn: async ({ enabled }) => {
      const response = await API.put('/order/update/', {
        OrderID: order_id,
        RoundingEnabled: enabled  // Backend will apply rounding if true
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('העיגול עודכן בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);

      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון העיגול');
      // Revert the checkbox state on error
      setRoundingEnabled(!roundingEnabled);
    },
  });

  // Create Product Mutation
  const createProductMutation = useMutation({
    mutationFn: async (productData) => {
      setCreatingProductWithInventory(true);
      
      try {
        // Step 1: Create the product
        const productResponse = await API.post('/products/create/', productData.product);
        const newProduct = productResponse.data;
        
        // Step 2: If storage enabled and inventory data provided, create inventory records
        if (storageEnabled && productData.inventory) {
          const inventoryPromises = [];
          
          for (const [storageId, data] of Object.entries(productData.inventory)) {
            if (data.quantity > 0) {
              inventoryPromises.push(
                API.post('/inventory/create/', {
                  ProductID: newProduct.ProductID,
                  StorageID: parseInt(storageId),
                  available_quantity: data.quantity
                })
              );
            }
          }
          
          if (inventoryPromises.length > 0) {
            await Promise.all(inventoryPromises);
          }
        }
        
        return { 
          product: newProduct, 
          selectedStorage: productData.selectedStorage 
        };
      } finally {
        setCreatingProductWithInventory(false);
      }
    },
    onSuccess: (data) => {
      setCreateProductSuccess(`${productLabel} נוצר בהצלחה`);
      
      // Refresh products list
      queryClient.invalidateQueries(['products']);
      
      // Create enhanced product object with inventory info
      const enhancedProduct = {
        ...data.product,
        inventory: storageEnabled ? Object.entries(newProductInventory)
          .filter(([_, data]) => data.quantity > 0)
          .map(([storageId, data]) => ({
            StorageID: parseInt(storageId),
            StorageName: data.storageName,
            available_quantity: data.quantity,
            is_default: data.is_default
          })) : []
      };
      
      // Create product entry with selected storage
      const newProductEntry = {
        product: enhancedProduct,
        quantity: Math.round(parseFloat(newProductData.Quantity) * 100) / 100 || 1,
        customPrice: null,
        entryId: Date.now(),
        storageId: data.selectedStorage?.StorageID || null,
        storageName: data.selectedStorage?.StorageName || null
      };
      
      // Add to the added products list
      setAddedProducts(prev => [...prev, newProductEntry]);
      
      // Reset form
      setNewProductData({
        ProductName: '',
        Description: '',
        Price: '',
        Quantity: ''
      });
      setNewProductInventory({});
      setSelectedStorageForNewProduct(null);
      
      // Exit creation mode
      setProductCreationMode(false);
      
      setTimeout(() => {
        setCreateProductSuccess('');
      }, 1500);
    },
    onError: (error) => {
      setCreateProductError(error?.response?.data?.error || `שגיאה ביצירת ${productLabel}`);
      setCreatingProductWithInventory(false);
    },
  });

  // Update Item Status Mutation
  const updateItemStatusMutation = useMutation({
    mutationFn: async (data) => {
      const response = await API.post('/order/item/status/', {
        OrderItemID: data.orderItemId,
        Status: data.status
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage('סטטוס הפריט עודכן בהצלחה');
      queryClient.invalidateQueries(['order', order_id]);
      setItemStatusDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון סטטוס הפריט');
    },
  });

  const fetchProductInventory = async (productId, storageId) => {
    try {
      const response = await API.get('/inventory/search/', {
        params: {
          search: productId,  // This will match the ProductID
          StorageID: storageId
        }
      });
      
      // Find the exact match for our product
      if (response.data && response.data.results) {
        const inventory = response.data.results.find(
          inv => inv.ProductID === parseInt(productId)
        );
        return inventory;
      }
      return null;
    } catch (error) {
      console.error('Error fetching inventory:', error);
      return null;
    }
  };

  const renderProductField = (fieldName, fieldConfig) => {
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
    
    // Skip readonly fields and excluded fields
    if (fieldConfig.readonly || excludedFields.includes(fieldName)) {
      return null;
    }
    
    const fieldType = fieldConfig.type || 'string';
    const label = fieldConfig.label || fieldName;
    const required = fieldConfig.required || false;
    
    switch (fieldType) {
      case 'number':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              type="number"
              required={required}
              variant="outlined"
              InputProps={{
                inputProps: { 
                  min: fieldConfig.min || 0, 
                  max: fieldConfig.max || undefined,
                  step: 0.01 
                }
              }}
            />
          </Grid>
        );
      
      case 'text':
        return (
          <Grid item xs={12} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              multiline
              rows={2}
              required={required}
              variant="outlined"
            />
          </Grid>
        );
        
      case 'boolean':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <FormControlLabel
              control={
                <Switch
                  checked={newProductData[fieldName] || false}
                  onChange={(e) => setNewProductData(prev => ({
                    ...prev,
                    [fieldName]: e.target.checked
                  }))}
                  name={fieldName}
                />
              }
              label={label}
            />
          </Grid>
        );
        
      case 'select':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              select
              required={required}
              variant="outlined"
            >
              <MenuItem value="">
                <em>בחר</em>
              </MenuItem>
              {fieldConfig.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
        
      case 'date':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              type="date"
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              required={required}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        );
        
      default: // string
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              required={required}
              variant="outlined"
            />
          </Grid>
        );
    }
  };

  const getProductFieldsInOrder = () => {
    const orderedFields = [];
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
    
    // Special order fields that have specific positions
    const specialOrderFields = [
      'ProductName',
      'CatalogNumber', 
      'Barcode',
      'Price',
      'Unit',
      'Description'
    ];
    
    // 1. Always show ProductName first
    orderedFields.push('ProductName');
    
    // 2. Add CatalogNumber if in product_fields
    if (productFields.includes('CatalogNumber') && 
        !excludedFields.includes('CatalogNumber') &&
        (!productFieldsSchema.CatalogNumber || !productFieldsSchema.CatalogNumber.readonly)) {
      orderedFields.push('CatalogNumber');
    }
    
    // 3. Add Barcode if in product_fields
    if (productFields.includes('Barcode') && 
        !excludedFields.includes('Barcode') &&
        (!productFieldsSchema.Barcode || !productFieldsSchema.Barcode.readonly)) {
      orderedFields.push('Barcode');
    }
    
    // 4. Always show Price
    orderedFields.push('Price');
    
    // 5. Add Unit if in product_fields
    if (productFields.includes('Unit') && 
        !excludedFields.includes('Unit') &&
        (!productFieldsSchema.Unit || !productFieldsSchema.Unit.readonly)) {
      orderedFields.push('Unit');
    }
    
    // 6. Add all other fields from product_fields (excluding already added and Description)
    productFields.forEach(fieldName => {
      if (!orderedFields.includes(fieldName) && 
          !excludedFields.includes(fieldName) &&
          !specialOrderFields.includes(fieldName) &&
          productFieldsSchema[fieldName] &&
          !productFieldsSchema[fieldName].readonly) {
        orderedFields.push(fieldName);
      }
    });
    
    // 7. Add Description last
    if ((productFields.includes('Description') || productFields.length === 0) && 
        !excludedFields.includes('Description') &&
        (!productFieldsSchema.Description || !productFieldsSchema.Description.readonly)) {
      orderedFields.push('Description');
    }
    
    return orderedFields;
  };


  const resetNewProductData = () => {
    const resetData = {
      ProductName: '',
      Description: '',
      Price: '0',
      Quantity: 1
    };
    
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
    Object.entries(productFieldsSchema).forEach(([fieldName, fieldConfig]) => {
      if (!fieldConfig.readonly && 
          !resetData.hasOwnProperty(fieldName) && 
          productFields.includes(fieldName) &&
          !excludedFields.includes(fieldName)) {
        resetData[fieldName] = fieldConfig.default || '';
      }
    });
    
    setNewProductData(resetData);
  };




  // Reset payment form
  const resetPaymentForm = () => {
    setPaymentMethod('cash');
    setPaymentAmount('');
    setPaymentAmountError('');
    setCheckNumber('');
    setCheckError('');
    setCheckDate(null);
    setCheckBankName('');
    setCheckAccountNumber('');
    setCardLastFour('');
    setCardType('');
    setBankName('');
    setBankAccountLast4('');
    setTransferDate(null);
    setPaymentNotes('');
    setErrorMessage('');
    setEditingPayment(null);
  };
  
  // Reset product edit form
  const resetProductEditForm = () => {
    setEditingProductItem(null);
    setProductQuantity(1);
    setProductUnitPrice('');
    setErrorMessage('');
  };
  
  // Open status dialog
  const handleStatusClick = () => {
    if (order.Status === 'cancelled') {
      setErrorMessage('לא ניתן לשנות סטטוס של הזמנה מבוטלת');
      return;
    }
    setNewStatus(order.Status);
    setErrorMessage('');
    setStatusDialogOpen(true);
  };
  
  // Open due date dialog
  const handleDueDateClick = () => {
    setNewDueDate(order.DueDate ? new Date(order.DueDate) : null);
    setErrorMessage('');
    setDueDateDialogOpen(true);
  };
  
  // NEW: Open order date dialog
  const handleOrderDateClick = () => {
    setNewOrderDate(order.OrderDate ? new Date(order.OrderDate) : null);
    setErrorMessage('');
    setOrderDateDialogOpen(true);
  };
  
  // Open delete dialog
  const handleDeleteClick = () => {
    setErrorMessage('');
    setDeleteDialogOpen(true);
  };
  
  // Open cancel dialog
  const handleCancelClick = () => {
    setErrorMessage('');
    setRefundPayments(true);
    setCancelNotes('');
    setCancelDialogOpen(true);
  };
  
  // Open payment dialog
  const handlePaymentClick = () => {
    setPaymentAmount('0'); // Changed to always use 0 as default
    setErrorMessage('');
    setPaymentDialogOpen(true);
  };
  
  // Open product dialog for edit
  const handleEditProduct = async (item) => {
    console.log('Editing product item:', item);
    
    setEditingProductItem(item);
    setProductQuantity(item.Quantity);
    setProductUnitPrice(item.UnitPrice.toString());
    setProductDialogMode('edit');
    setErrorMessage('');
    
    // Only fetch inventory if storage is enabled
    if (storageEnabled) {
      setEditingProductInventory(null); // Reset first
      
      const productId = item.ProductID || item.Product;
      const storageId = item.StorageID;
      
      if (productId && storageId) {
        try {
          const inventory = await fetchProductInventory(productId, storageId);
          
          if (inventory) {
            // Show what's actually available in storage (not including current allocation)
            const availableInStorage = parseFloat(inventory.available_quantity);
            const currentItemQuantity = parseFloat(item.Quantity);
            
            setEditingProductInventory({
              storageName: inventory.StorageName || item.StorageName,
              availableInStorage: availableInStorage,  // What's actually free in storage
              currentAllocated: currentItemQuantity,    // What this order item has
              maxQuantity: availableInStorage + currentItemQuantity  // Max they can set
            });
          } else {
            // No inventory record found
            setEditingProductInventory({
              storageName: item.StorageName || 'מחסן',
              availableInStorage: 0,
              currentAllocated: parseFloat(item.Quantity),
              maxQuantity: parseFloat(item.Quantity)  // Can't increase, only decrease
            });
          }
        } catch (error) {
          console.error('Failed to fetch inventory:', error);
        }
      }
    }
    
    setProductEditDialogOpen(true);
  };

  const handleCancelProductDialog = () => {
    setAddedProducts([]);
    setAddProductDialogOpen(false);
    setProductCreationMode(false);
    setCreateProductError('');
    setCreateProductSuccess('');
    setSelectedProduct(null);
    setSelectedStorage(null);
    setAvailableStorages([]);
    setErrorMessage('');
    setNoInventoryWarning(false);
    setProductAutocompleteOpen(false);
  };

  // Remove product from added products list
  const removeFromAddedProducts = (index) => {
    setAddedProducts(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleFinishAddingProducts = () => {
    if (isFinishingProductsRef.current) return; // Prevent double-click (sync check)
    isFinishingProductsRef.current = true;
    setIsFinishingProducts(true);

    // Collect all items to add and update in arrays for a single batch request
    // This prevents race conditions and ensures a single transaction/debt update
    const itemsToAdd = [];
    const itemsToUpdate = [];

    // Process each product in the added products list
    addedProducts.forEach(newEntry => {
      // Check if this product*price*storage combination already exists in the order
      const existingItem = order.items.find(item => {
        const sameProduct = String(item.ProductID) === String(newEntry.product.ProductID);
        const samePrice = Math.abs(Number(item.UnitPrice) - Number(newEntry.customPrice || newEntry.product.Price)) < 0.001;
        const sameStorage = storageEnabled
          ? item.StorageID === newEntry.storageId
          : true; // If storage not enabled, ignore storage in comparison

        return sameProduct && samePrice && sameStorage;
      });

      if (existingItem) {
        // Update existing item quantity
        const updateData = {
          OrderItemID: existingItem.OrderItemID,
          Quantity: Math.round((parseFloat(existingItem.Quantity) + newEntry.quantity) * 100) / 100,
          UnitPrice: Math.round(parseFloat(existingItem.UnitPrice) * 100) / 100,
        };

        // Include storage if enabled
        if (storageEnabled && newEntry.storageId) {
          updateData.StorageID = newEntry.storageId;
        }

        itemsToUpdate.push(updateData);
      } else {
        // Add as new item
        const addData = {
          ProductID: newEntry.product.ProductID,
          Quantity: Math.round(newEntry.quantity * 100) / 100,
          UnitPrice: Math.round((newEntry.customPrice || newEntry.product.Price) * 100) / 100,
        };

        // Include storage if enabled
        if (storageEnabled && newEntry.storageId) {
          addData.StorageID = newEntry.storageId;
        }

        itemsToAdd.push(addData);
      }
    });

    // Submit all items in a single batch request
    if (itemsToAdd.length > 0 || itemsToUpdate.length > 0) {
      batchProductsMutation.mutate({ itemsToAdd, itemsToUpdate });
    } else {
      // No items to process, just close dialog
      setAddedProducts([]);
      setAddProductDialogOpen(false);
      setProductAutocompleteOpen(false);
      setSelectedStorage(null);
      setAvailableStorages([]);
      isFinishingProductsRef.current = false;
      setIsFinishingProducts(false);
    }
  };

  // Open product dialog for add
  const handleAddProductClick = () => {
    setProductDialogMode('add');
    setSelectedProduct(null);
    setProductQuantity(1);
    setProductUnitPrice('');
    setAddedProducts([]);
    setSearchTerm('');
    setProductCreationMode(false);
    setErrorMessage('');
    setAddProductDialogOpen(true);
    setProductAutocompleteOpen(true);
  };
  
  // Handle product selection
  const handleProductSelect = async (event, newValue) => {
    if (newValue) {
      setNoInventoryWarning(false); // Reset warning
      setSelectedProduct(newValue);
      
      if (storageEnabled) {
        try {
          const fullProductData = await fetchProductDetails(newValue.ProductID);
          setSelectedProduct(fullProductData);
          setProductUnitPrice(fullProductData.Price.toString());
          
          if (fullProductData.inventory && fullProductData.inventory.length > 0) {
            const storagesWithInventory = fullProductData.inventory.filter(inv => inv.available_quantity > 0);
            
            if (storagesWithInventory.length === 0) {
              // No inventory available
              setNoInventoryWarning(true);
              setAvailableStorages([]);
              setSelectedStorage(null);
            } else {
              setAvailableStorages(storagesWithInventory);
              const defaultStorage = storagesWithInventory.find(inv => inv.is_default);
              setSelectedStorage(defaultStorage || storagesWithInventory[0]);
            }
          } else {
            // No inventory records
            setNoInventoryWarning(true);
            setAvailableStorages([]);
            setSelectedStorage(null);
          }
        } catch (error) {
          console.error('Failed to fetch product details:', error);
          setErrorMessage('שגיאה בטעינת פרטי המוצר');
        }
        // Focus quantity field after product selection
        setTimeout(() => {
          quantityInputRef.current?.focus();
          quantityInputRef.current?.select();
        }, 150);
      } else {
        setProductUnitPrice(newValue.Price.toString());
        // Focus quantity field after product selection
        setTimeout(() => {
          quantityInputRef.current?.focus();
          quantityInputRef.current?.select();
        }, 150);
      }
    } else {
      setSelectedProduct(null);
      setProductUnitPrice('');
      setAvailableStorages([]);
      setSelectedStorage(null);
      setNoInventoryWarning(false);
    }
  };
  // Handle cancel product edit
  const handleCancelProductEdit = () => {
    resetProductEditForm();
    if (storageEnabled) {
      setEditingProductInventory(null);
    }
    setProductEditDialogOpen(false);
  };
  // Handler for deleting a product
  const handleDeleteProduct = (productId) => {
    setDeleteProductId(productId);
    setErrorMessage('');
    setDeleteProductDialogOpen(true);
  };
  
  // Confirm product deletion
  const handleConfirmDeleteProduct = () => {
    if (!deleteProductId) return;
    deleteProductMutation.mutate(deleteProductId);
  };
  
  // Handler for saving product edits
  const handleSaveProductEdit = () => {
    if (!editingProductItem) return;
    
    // Basic validation
    if (productQuantity <= 0) {
      setErrorMessage('הכמות חייבת להיות גדולה מאפס');
      return;
    }
    
    if (parseFloat(productUnitPrice) < 0) {
      setErrorMessage(`מחיר ה${productLabel} חייב להיות גדול שווה מאפס`);
      return;
    }
    
    // Inventory validation ONLY if storage is enabled
    if (storageEnabled && editingProductInventory) {
      if (productQuantity > editingProductInventory.maxQuantity) {
        setErrorMessage(
          `הכמות המבוקשת (${productQuantity}) עולה על המלאי הזמין. ` +
          `מלאי פנוי: ${editingProductInventory.availableInStorage}, ` +
          `מוקצה כעת: ${editingProductInventory.currentAllocated}`
        );
        return;
      }
    }
    
    // Submit update.
    // parseFloat('') is NaN and JSON.stringify serializes NaN as null, which the
    // backend cannot parse as a number — validate before building the payload.
    const parsedUnitPrice = parseFloat(productUnitPrice);
    const parsedQuantity = parseFloat(productQuantity);

    if (!Number.isFinite(parsedUnitPrice) || parsedUnitPrice < 0) {
      setErrorMessage('יש להזין מחיר ליחידה תקין');
      return;
    }

    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setErrorMessage('יש להזין כמות תקינה');
      return;
    }

    const updateData = {
      OrderItemID: editingProductItem.OrderItemID,
      Quantity: Math.round(parsedQuantity * 100) / 100,
      UnitPrice: Math.round(parsedUnitPrice * 100) / 100
    };
    
    updateProductMutation.mutate(updateData);
  };

  const fetchProductDetails = async (productId) => {
    try {
      const response = await API.get('/product/', {
        params: { ProductID: productId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  };

  // Open the data-grid inventory dialog (same UX as products page) for
  // the currently-editing order item. We fetch the full product so the
  // dialog has the embedded inventory array it expects.
  const handleOpenAdjustInventory = async () => {
    if (!editingProductItem) return;
    if (!canAdjustInventory) {
      setErrorMessage('יש לפנות למנהל לעדכון המלאי');
      return;
    }
    try {
      const productId = editingProductItem.ProductID || editingProductItem.Product;
      const full = await fetchProductDetails(productId);
      setAdjustInventoryProduct(full);
      setAdjustInventoryOpen(true);
    } catch (e) {
      setErrorMessage('שגיאה בטעינת פרטי המוצר לעדכון מלאי');
    }
  };

  // After the data-grid dialog saves, refresh the Alert values in the
  // Edit Product dialog so the available quantity + max constraint
  // reflect the new stock without closing the edit flow.
  const handleInventoryAdjusted = async () => {
    setAdjustInventoryOpen(false);
    if (!editingProductItem) return;
    const productId = editingProductItem.ProductID || editingProductItem.Product;
    const storageId = editingProductItem.StorageID;
    if (!productId || !storageId) return;
    try {
      const inventory = await fetchProductInventory(productId, storageId);
      if (inventory) {
        const availableInStorage = parseFloat(inventory.available_quantity);
        const currentItemQuantity = parseFloat(editingProductItem.Quantity);
        setEditingProductInventory({
          storageName: inventory.StorageName || editingProductItem.StorageName,
          availableInStorage,
          currentAllocated: currentItemQuantity,
          maxQuantity: availableInStorage + currentItemQuantity,
        });
        setErrorMessage('');
      }
      queryClient.invalidateQueries(['products']);
    } catch (e) {
      console.error('Failed to refresh inventory after adjust:', e);
    }
  };

  // 3. Add the enhanced filter function (add after line 700 or before the mutations)
  const filterProductsEnhanced = useCallback((options, params) => {
    const { inputValue } = params;
    const input = inputValue.toLowerCase().trim();
    
    if (!input) {
      return options.slice(0, 20);
    }
    
    if (input.length < 2) {
      return [];
    }
    
    // Check if barcode and catalog number are in the UI config
    const showBarcode = productFields.includes('Barcode');
    const showCatalogNumber = productFields.includes('CatalogNumber');
    const showEnhancedSearch = showBarcode || showCatalogNumber || storageEnabled;
    
    const filtered = options.map(product => {
      const productName = (product.ProductName || '').toLowerCase();
      const productId = (product.ProductID || '').toString();
      
      // Basic search (always active)
      let matches = productName.includes(input) || productId.includes(input);
      let matchedField = null;
      
      // Enhanced search (only if fields are in UI config or storage enabled)
      if (showEnhancedSearch) {
        const barcode = showBarcode ? (product.Barcode || '').toLowerCase() : '';
        const catalogNumber = showCatalogNumber ? (product.CatalogNumber || '').toLowerCase() : '';
        
        // Check which field matches
        if (productName.includes(input)) {
          matchedField = productFieldsSchema.ProductName?.label || 'שם מוצר';
        } else if (showCatalogNumber && catalogNumber.includes(input)) {
          matchedField = productFieldsSchema.CatalogNumber?.label || 'מק"ט';
        } else if (showBarcode && barcode.includes(input)) {
          matchedField = productFieldsSchema.Barcode?.label || 'ברקוד';
        } else if (productId.includes(input)) {
          matchedField = 'מזהה';
        }
        
        matches = matches || 
                  (showBarcode && barcode.includes(input)) || 
                  (showCatalogNumber && catalogNumber.includes(input));
      }
      
      if (matches) {
        return showEnhancedSearch ? { ...product, matchedField } : product;
      }
      return null;
    }).filter(Boolean);
    
    return filtered.slice(0, 50);
  }, [productFields, productFieldsSchema, storageEnabled]);

  // Handler for adding a new product to the order
  const handleAddProductToOrder = () => {
    // First, check if we have a selected product or newly created ones
    if (addedProducts.length > 0) {
      // Process each added product
      addedProducts.forEach(entry => {
        // Check if this product*price*storage combination already exists in the order items
        const existingItem = order.items.find(item => {
          // Convert both IDs to strings for consistent comparison
          const itemProductID = String(item.ProductID);
          const entryProductID = String(entry.product.ProductID);
          const price = entry.customPrice || entry.product.Price;
          
          // Check product ID and price match
          const sameProduct = itemProductID === entryProductID;
          const samePrice = Math.abs((Number(item.UnitPrice) || 0) - (Number(price) || 0)) < 0.001;
          
          // Check storage match if storage is enabled
          const sameStorage = storageEnabled 
            ? item.StorageID === entry.storageId
            : true; // If storage not enabled, ignore storage in comparison
          
          return sameProduct && samePrice && sameStorage;
        });
        
        if (existingItem) {
          // If item exists with same price and storage, update its quantity instead
          const updateData = {
            OrderItemID: existingItem.OrderItemID,
            Quantity: Math.round((parseFloat(existingItem.Quantity) + parseFloat(entry.quantity)) * 100) / 100,
            UnitPrice: Math.round(parseFloat(existingItem.UnitPrice) * 100) / 100,
            // Include storage if enabled
            ...(storageEnabled && entry.storageId && { StorageID: entry.storageId })
          };
          
          // Submit update to existing item
          updateProductMutation.mutate(updateData);
        } else {
          // If no match found, add as new item
          const addData = {
            ProductID: entry.product.ProductID,
            Quantity: Math.round(entry.quantity * 100) / 100,
            UnitPrice: Math.round((entry.customPrice || entry.product.Price) * 100) / 100,
            // Include storage if enabled
            ...(storageEnabled && entry.storageId && { StorageID: entry.storageId })
          };
          
          // Submit add request
          addProductMutation.mutate(addData);
        }
      });
    } else if (selectedProduct) {
      // Process the selected product
      const price = parseFloat(productUnitPrice) || selectedProduct.Price;
      
      // Check if this product*price*storage combination already exists in the order items
      const existingItem = order.items.find(item => {
        // Convert both IDs to strings for consistent comparison
        const itemProductID = String(item.ProductID);
        const selectedProductID = String(selectedProduct.ProductID);
        
        // Check product ID and price match
        const sameProduct = itemProductID === selectedProductID;
        const samePrice = Math.abs((Number(item.UnitPrice) || 0) - (Number(price) || 0)) < 0.001;
        
        // Check storage match if storage is enabled
        const sameStorage = storageEnabled 
          ? item.StorageID === (selectedStorage?.StorageID || null)
          : true; // If storage not enabled, ignore storage in comparison
        
        return sameProduct && samePrice && sameStorage;
      });
      
      if (existingItem) {
        // If item exists with same price and storage, update its quantity instead
        const updateData = {
          OrderItemID: existingItem.OrderItemID,
          Quantity: parseFloat(existingItem.Quantity) + parseFloat(productQuantity),
          UnitPrice: existingItem.UnitPrice,
          // Include storage if enabled
          ...(storageEnabled && selectedStorage && { StorageID: selectedStorage.StorageID })
        };
        
        // Submit update to existing item
        updateProductMutation.mutate(updateData);
      } else {
        // If no match found, add as new item
        const addData = {
          ProductID: selectedProduct.ProductID,
          Quantity: productQuantity,
          UnitPrice: price,
          // Include storage if enabled
          ...(storageEnabled && selectedStorage && { StorageID: selectedStorage.StorageID })
        };
        
        // Submit add request
        addProductMutation.mutate(addData);
      }
    } else {
      setErrorMessage(`נא לבחור ${productLabel} להוספה`);
    }
    
    // Reset and close dialog after processing
    setAddProductDialogOpen(false);
    setAddedProducts([]);
    setSelectedStorage(null);
    setAvailableStorages([]);
  };
  
  // Handler for creating a new product
  const handleCreateProduct = () => {
    setCreateProductError('');
    
    // Basic validation
    if (!newProductData.ProductName) {
      setCreateProductError(`נא להזין שם ${productLabel}`);
      return;
    }
    
    const price = newProductData.Price === '' ? 0 : parseFloat(newProductData.Price);
    if (isNaN(price) || price < 0) {
      setCreateProductError('נא להזין מחיר תקין (0 או יותר)');
      return;
    }
    
    if (!newProductData.Quantity || isNaN(parseFloat(newProductData.Quantity)) || parseFloat(newProductData.Quantity) <= 0) {
      setCreateProductError('נא להזין כמות תקינה');
      return;
    }
    
    if (storageEnabled && selectedStorageForNewProduct) {
      const orderQuantity = parseFloat(newProductData.Quantity);
      const storageInventory = newProductInventory[selectedStorageForNewProduct.StorageID]?.quantity || 0;
      
      if (orderQuantity > storageInventory) {
        setCreateProductError(
          `הכמות להזמנה (${orderQuantity}) גדולה מהמלאי שהוגדר למחסן ${selectedStorageForNewProduct.StorageName} (${storageInventory}). ` +
          `נא להגדיל את המלאי במחסן או להקטין את הכמות להזמנה.`
        );
        return;
      }
      
      if (storageInventory === 0) {
        setCreateProductError(
          `לא הוגדר מלאי במחסן ${selectedStorageForNewProduct.StorageName}. ` +
          `נא להגדיר כמות מלאי במחסן הנבחר.`
        );
        return;
      }
    }
    
    // Validate other required fields from schema
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
    const requiredFields = Object.entries(productFieldsSchema)
      .filter(([fieldName, config]) => 
        config.required && 
        productFields.includes(fieldName) && 
        !excludedFields.includes(fieldName) &&
        fieldName !== 'ProductName' && 
        fieldName !== 'Price'
      );
    
    for (const [fieldName, config] of requiredFields) {
      if (!newProductData[fieldName] || newProductData[fieldName].toString().trim() === '') {
        setCreateProductError(`${config.label || fieldName} הוא שדה חובה`);
        return;
      }
    }
    
    // Prepare data for submission - only send non-empty values
    const productData = {};
    Object.entries(newProductData).forEach(([key, value]) => {
      if (key !== 'Quantity' && value !== '' && value !== null && value !== undefined) {
        if (key === 'Price') {
          productData[key] = Math.round(price * 100) / 100;
        } else {
          productData[key] = value;
        }
      }
    });
    
    const submitData = {
      product: productData,
      inventory: storageEnabled ? newProductInventory : null,
      selectedStorage: storageEnabled ? selectedStorageForNewProduct : null
    };
    
    createProductMutation.mutate(submitData);
  };
  
  // Handler for toggling product creation mode
  const toggleProductCreationMode = () => {
    setProductCreationMode(!productCreationMode);
    setCreateProductError('');
    setCreateProductSuccess('');
    setNewProductData({
      ProductName: '',
      Description: '',
      Price: '',
      Quantity: 1
    });
  };
  
  // Handler for new product data changes
  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProductData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handler for single product add to order
  const handleAddProductToCurrent = () => {
    if (!selectedProduct || isAddingProductRef.current) return;
    isAddingProductRef.current = true;
    setIsAddingProduct(true);

    if (storageEnabled && noInventoryWarning) {
      setErrorMessage(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
      isAddingProductRef.current = false;
      setIsAddingProduct(false);
      return;
    }

    if (!productQuantity || parseFloat(productQuantity) <= 0) {
      setErrorMessage('נא להזין כמות');
      isAddingProductRef.current = false;
      setIsAddingProduct(false);
      return;
    }
    const price = parseFloat(productUnitPrice) || selectedProduct.Price;
    const isCustomPrice = Math.abs(Number(price) - Number(selectedProduct.Price)) > 0.001;
    const qty = parseFloat(productQuantity) || 0;
    
    // Validation for storage if enabled
    if (storageEnabled && selectedStorage) {
      if (qty > selectedStorage.available_quantity) {
        setErrorMessage(`הכמות המבוקשת (${qty}) עולה על המלאי הזמין (${selectedStorage.available_quantity})`);
        isAddingProductRef.current = false;
        setIsAddingProduct(false);
        return;
      }
    }
    
    // Check if the same product with same price AND same storage already exists
    const existingIndex = addedProducts.findIndex(entry => {
      const sameProduct = entry.product.ProductID === selectedProduct.ProductID;
      const samePrice = ((!entry.customPrice && !isCustomPrice) || 
                        (entry.customPrice && isCustomPrice && 
                        Math.abs(Number(entry.customPrice) - Number(price)) < 0.001));
      const sameStorage = storageEnabled 
        ? entry.storageId === (selectedStorage?.StorageID || null)
        : true;
      
      return sameProduct && samePrice && sameStorage;
    });
    
    if (existingIndex >= 0) {
      // Update existing entry - sum the quantities
      const updatedProducts = [...addedProducts];
      updatedProducts[existingIndex].quantity = 
        Math.round((parseFloat(updatedProducts[existingIndex].quantity) + qty) * 100) / 100;
      setAddedProducts(updatedProducts);
    } else {
      // Add as new entry
      const newProduct = {
        product: selectedProduct,
        quantity: Math.round(qty * 100) / 100,
        customPrice: isCustomPrice ? price : null,
        storageId: selectedStorage?.StorageID || null,
        storageName: selectedStorage?.StorageName || null,
        entryId: Date.now()
      };
      
      setAddedProducts(prev => [...prev, newProduct]);  // Append to existing array
    }
    
    // Reset selection for next product
    setSelectedProduct(null);
    setProductQuantity(1);
    setProductUnitPrice('');
    setSearchTerm('');
    setSelectedStorage(null);
    setAvailableStorages([]);
    setErrorMessage('');
    isAddingProductRef.current = false;
    setIsAddingProduct(false);

    // Open dropdown and focus search for next product selection
    setProductAutocompleteOpen(true);
    setTimeout(() => {
      productSearchInputRef.current?.focus();
    }, 100);
  };

  // Handler for notes expansion toggle
  const toggleNotesExpanded = () => {
    setNotesExpanded(!notesExpanded);
  };
  
  // Handler for expanded dialog notes toggle
  const toggleExpandedNotesDialog = (editing = false) => {
    setExpandedNotesDialog(!expandedNotesDialog);
    setEditingNotes(editing);
    // Reset error/success messages when opening dialog
    if (!expandedNotesDialog) {
      setNotesErrorMsg('');
      setNotesSuccessMsg('');
    }
  };
  
  // Handler for editing notes
  const handleEditNotes = () => {
    setEditingNotes(true);
  };
  
  // Enhanced handler for saving notes
  const handleSaveNotes = () => {
    setNotesLoadingState(true);
    setNotesErrorMsg('');
    setNotesSuccessMsg('');

    updateNotesMutation.mutate(notesValue, {
      onSuccess: () => {
        setNotesSuccessMsg('ההערות עודכנו בהצלחה');
        setEditingNotes(false);
        setNotesLoadingState(false);
        
        // Clear success message after delay
        setTimeout(() => {
          setNotesSuccessMsg('');
        }, 3000);
      },
      onError: (error) => {
        setNotesErrorMsg(error?.response?.data?.error || 'שגיאה בעדכון ההערות');
        setNotesLoadingState(false);
      }
    });
  };
  
  // Handler for canceling notes editing
  const handleCancelEditNotes = () => {
    setNotesValue(order.Notes || '');
    setEditingNotes(false);
    setNotesErrorMsg('');
    setNotesSuccessMsg('');
  };
  
  // Handler for updating due date
  const handleDueDateUpdate = () => {
    updateDueDateMutation.mutate(newDueDate);
  };
  
  // NEW: Handler for updating order date
  const handleOrderDateUpdate = () => {
    updateOrderDateMutation.mutate(newOrderDate);
  };

  // Handler for toggling rounding
  const handleRoundingToggle = (event) => {
    const enabled = event.target.checked;

    // Check if order is not fully paid
    if (order && order.PaidAmount >= order.TotalAmount && enabled) {
      setErrorMessage('לא ניתן לשנות עיגול בהזמנה ששולמה במלואה');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    // Update state optimistically
    setRoundingEnabled(enabled);

    // Call mutation to update backend
    updateRoundingMutation.mutate({ enabled });
  };

  // Handler for opening edit payment dialog
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentMethod(payment.PaymentMethod);
    setPaymentAmount(payment.Amount.toString());
    setPaymentNotes(payment.Notes || '');
    
    // Set method-specific fields
    if (payment.PaymentMethod === 'check') {
      setCheckNumber(payment.CheckNumber || '');
      setCheckBankName(payment.CheckBankName || '');
      setCheckAccountNumber(payment.CheckAccountNumber || '');
      setCheckDate(payment.CheckDate ? new Date(payment.CheckDate) : null);
    } else if (payment.PaymentMethod === 'credit_card') {
      setCardLastFour(payment.CardLastFour || '');
      setCardType(payment.CardType || '');
    } else if (payment.PaymentMethod === 'bank_transfer') {
      setBankName(payment.BankName || '');
      setBankAccountLast4(payment.BankAccountLast4 || '');
      setTransferDate(payment.TransferDate ? new Date(payment.TransferDate) : null);
    }
    
    setErrorMessage('');
    setEditPaymentDialogOpen(true);
  };
  
  // Handler for opening delete payment dialog
  const handleDeletePayment = (paymentId) => {
    setDeletePaymentId(paymentId);
    setErrorMessage('');
    setDeletePaymentDialogOpen(true);
  };
  
  // Open item status dialog
  const handleItemStatusClick = (item) => {
    if (order.Status === 'cancelled') {
      setErrorMessage('לא ניתן לשנות סטטוס פריטים בהזמנה מבוטלת');
      return;
    }
    setEditingItemStatus(item);
    setNewItemStatus(item.Status || 'not_supplied');
    setErrorMessage('');
    setItemStatusDialogOpen(true);
  };

  // NEW: Update item status
  const handleItemStatusUpdate = () => {
    if (!editingItemStatus || !newItemStatus) {
      setErrorMessage('יש לבחור סטטוס');
      return;
    }
    
    updateItemStatusMutation.mutate({
      orderItemId: editingItemStatus.OrderItemID,
      status: newItemStatus
    });
  };

  // NEW: Get item status color
  const getItemStatusColor = (status) => {
    switch (status) {
      case 'supplied':
        return { bg: '#e8f5e9', color: '#4caf50' };
      case 'not_supplied':
        return { bg: '#ffebee', color: '#f44336' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Improved product search functionality
  useEffect(() => {
    if (products.length > 0) {
      let filtered = [...products];
      
      if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase().trim();
        filtered = products.filter(product => 
          product.ProductName.toLowerCase().includes(lowerSearch) ||
          product.ProductID.toString().includes(lowerSearch) ||
          (product.Description && product.Description.toLowerCase().includes(lowerSearch))
        );
      }
      
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [searchTerm, products]);
  
  // Auto-clear notes success message after 3 seconds
  useEffect(() => {
    if (notesSuccessMsg) {
      const timer = setTimeout(() => setNotesSuccessMsg(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [notesSuccessMsg]);
  
  // Update status
  const handleStatusUpdate = () => {
    if (!newStatus) {
      setErrorMessage('יש לבחור סטטוס');
      return;
    }
    
    updateStatusMutation.mutate(newStatus);
  };
  
  // Delete order
  const handleDeleteConfirm = () => {
    deleteOrderMutation.mutate();
  };
  
  // Cancel order
  const handleCancelConfirm = () => {
    cancelOrderMutation.mutate();
  };
  
  // Add payment
  const handleAddPayment = () => {
    setErrorMessage('');
    setPaymentAmountError('');
    setCheckError('');
    
    // Validate payment amount
    if (!paymentAmount || isNaN(parseFloat(paymentAmount)) || parseFloat(paymentAmount) <= 0) {
      setPaymentAmountError('יש להזין סכום תשלום תקין');
      return;
    }
    
    // Validate check number if payment method is check
    // if (paymentMethod === 'check' && !checkNumber) {
    //   setCheckError('יש להזין מספר צ\'ק');
    //   return;
    // }
    
    // Create payment data
    const paymentData = {
      Order: order_id,
      Amount: parseFloat(paymentAmount),
      PaymentMethod: paymentMethod,
      Notes: paymentNotes
    };
    
    // Add method-specific fields
    if (paymentMethod === 'check') {
      paymentData.CheckNumber = checkNumber;
      if (checkDate) {
        paymentData.CheckDate = format(checkDate, 'yyyy-MM-dd');
      }
      if (checkBankName) {
        paymentData.CheckBankName = checkBankName;
      }
      if (checkAccountNumber) {
        paymentData.CheckAccountNumber = checkAccountNumber;
      }
    } else if (paymentMethod === 'credit_card') {
      if (cardLastFour) {
        paymentData.CardLastFour = cardLastFour;
      }
      if (cardType) {
        paymentData.CardType = cardType;
      }
    } else if (paymentMethod === 'bank_transfer') {
      if (bankName) {
        paymentData.BankName = bankName;
      }
      if (bankAccountLast4) {
        paymentData.BankAccountLast4 = bankAccountLast4;
      }
      if (transferDate) {
        paymentData.TransferDate = format( transferDate, 'yyyy-MM-dd');
      }
    }
    
    // Submit payment
    addPaymentMutation.mutate(paymentData);
  };
  
  // Edit payment
  const handleSavePaymentEdit = () => {
    setErrorMessage('');
    setPaymentAmountError('');
    setCheckError('');
    
    // Validate payment amount
    if (!paymentAmount || isNaN(parseFloat(paymentAmount)) || parseFloat(paymentAmount) <= 0) {
      setPaymentAmountError('יש להזין סכום תשלום תקין');
      return;
    }
    
    // Validate check number if payment method is check
    // if (paymentMethod === 'check' && !checkNumber) {
    //   setCheckError('יש להזין מספר צ\'ק');
    //   return;
    // }
    
    // Create payment data
    const paymentData = {
      PaymentID: editingPayment.PaymentID,
      Amount: parseFloat(paymentAmount),
      PaymentMethod: paymentMethod,
      Notes: paymentNotes
    };
    
    // Add method-specific fields
    if (paymentMethod === 'check') {
      paymentData.CheckNumber = checkNumber;
      if (checkDate) {
        paymentData.CheckDate = format(checkDate, 'yyyy-MM-dd');
      }
      if (checkBankName) {
        paymentData.CheckBankName = checkBankName;
      }
      if (checkAccountNumber) {
        paymentData.CheckAccountNumber = checkAccountNumber;
      }
    } else if (paymentMethod === 'credit_card') {
      if (cardLastFour) {
        paymentData.CardLastFour = cardLastFour;
      }
      if (cardType) {
        paymentData.CardType = cardType;
      }
    } else if (paymentMethod === 'bank_transfer') {
      if (bankName) {
        paymentData.BankName = bankName;
      }
      if (bankAccountLast4) {
        paymentData.BankAccountLast4 = bankAccountLast4;
      }
      if (transferDate) {
        paymentData.TransferDate = format(transferDate, 'yyyy-MM-dd');
      }
    }
    
    // Submit payment edit
    editPaymentMutation.mutate(paymentData);
  };
  
  // Delete payment
  const handleConfirmDeletePayment = () => {
    if (!deletePaymentId) return;
    deletePaymentMutation.mutate(deletePaymentId);
  };
  
  // Format currency
  const formatCurrency = (num) =>
    new Intl.NumberFormat('he-IL', { style:'currency', currency:'ILS' }).format(num);
  
  // Get status display name
  const getStatusName = (status) => {
    const statusOptions = orderFieldsSchema.Status?.options || [
      { value: 'pending', label: 'בתהליך' },
      { value: 'processing', label: 'בטיפול' },
      { value: 'completed', label: 'הושלם' },
      { value: 'cancelled', label: 'בוטל' },
    ];
    const option = statusOptions.find(opt => opt.value === status);
    return option?.label || status;
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3e0', color: '#ff9800' };
      case 'processing':
        return { bg: '#e1f5fe', color: '#0288d1' };
      case 'completed':
        return { bg: '#e8f5e9', color: '#4caf50' };
      case 'cancelled':
        return { bg: '#ffebee', color: '#f44336' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Print order function - modified to use popup and auto-close after printing
  const handlePrintOrder = () => {
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl">
        <head>
          <title>${orderLabel} מס' ${order.OrderNumber}</title>
          <meta charset="utf-8" />
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              direction: rtl;
            }
            h1, h2 {
              margin-bottom: 10px;
            }
            .header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 10px;
            }
            .section {
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: right;
            }
            th {
              background-color: #f2f2f2;
            }
            .total-row {
              font-weight: bold;
            }
            .payment-status {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 4px;
              font-weight: bold;
              margin-right: 5px;
            }
            .item-status {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 0.9em;
            }
            .item-status.supplied {
              background-color: #e8f5e9;
              color: #4caf50;
            }
            .item-status.not-supplied {
              background-color: #ffebee;
              color: #f44336;
            }
            .item-barcode {
              margin-top: 6px;
              line-height: 0;
            }
            .item-barcode svg {
              display: block;
              max-width: 220px;
              height: auto;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 0.8em;
              color: #666;
            }
            @media print {
              .no-print {
                display: none;
              }
              button {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          ${generateBusinessHeader(businessProfile)}
          <div class="header">
            <div>
              <h1>${orderLabel} מס' ${order.OrderNumber}</h1>
              <p>תאריך: ${new Date(order.OrderDate).toLocaleDateString('he-IL')}</p>
              ${orderFields.includes("DueDate") && order.DueDate ? `<p>תאריך אספקה: ${new Date(order.DueDate).toLocaleDateString('he-IL')}</p>` : ''}
            </div>
            <div>
              ${orderFields.includes("Status") ? `<p><strong>סטטוס:</strong> ${getStatusName(order.Status)}</p>` : ''}
              ${order.InvoiceNumber ? `<p><strong>מספר חשבונית:</strong> ${order.InvoiceNumber}</p>` : ''}
            </div>
          </div>
          
          <div class="section">
            <h2>פרטי לקוח</h2>
            <p><strong>שם:</strong> ${order.ClientName}</p>
            <p><strong>מספר לקוח:</strong> ${order.ClientBusinessID}</p>
          </div>
          
          <div class="section">
            <h2>${productPluralLabel}</h2>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>שם ${productLabel}</th>
                  <th>כמות</th>
                  <th>מחיר יחידה</th>
                  <th>סה"כ</th>
                  ${orderItemFields.includes("Status") ? '<th>סטטוס</th>' : ''}
                </tr>
              </thead>
              <tbody>
                ${order.items.map((item, index) => {
                  // Per-item extras come from
                  // ui_config.pages_configs.OrderDetailPage.print_item_fields.
                  // Opt-in list — e.g. ["Barcode"] prints the barcode SVG.
                  const printBarcode = orderPrintItemFields.includes('Barcode');
                  const barcodeSvg = (printBarcode && item.ProductBarcode)
                    ? generateBarcodeSVGString(item.ProductBarcode, {
                        width: 1.4, height: 28, fontSize: 9, textMargin: 1, margin: 2,
                        preferredSymbology: barcodePrintSymbology,
                      })
                    : '';
                  return `
                  <tr>
                     <td>${index + 1}</td>
                    <td>
                      ${item.ProductName}
                      ${barcodeSvg ? `<div class="item-barcode">${barcodeSvg}</div>` : ''}
                    </td>
                    <td>${item.Quantity}</td>
                    <td>${new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(item.UnitPrice)}</td>
                    <td>${new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(item.Subtotal)}</td>
                    ${orderItemFields.includes("Status") ? `
                      <td>
                        <span class="item-status ${item.Status === 'supplied' ? 'supplied' : 'not-supplied'}">
                          ${item.StatusDisplay || (item.Status === 'supplied' ? 'סופק' : 'לא סופק')}
                        </span>
                      </td>
                    ` : ''}
                  </tr>
                  `;
                }).join('')}
                ${order.Subtotal !== order.TotalAmount ? `
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left;"><strong>סה"כ לפני הנחה:</strong></td>
                  <td>${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(order.Subtotal)}</td>
                </tr>
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left;"><strong>הנחה:</strong></td>
                  <td>${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(order.DiscountAmount)}</td>
                </tr>
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left;"><strong>סה"כ אחרי הנחה:</strong></td>
                  <td>${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format((order.Subtotal || 0) - (order.DiscountAmount || 0))}</td>
                </tr>
                ` : ''}
                ${order.IsTaxable && order.IsTaxIncluded && order.TaxAmount > 0 ? `
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left; color: #666; font-size: 0.9em;">מזה מע"מ (${order.TaxRate || 18}%):</td>
                  <td style="color: #666; font-size: 0.9em;">${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(order.TaxAmount)}</td>
                </tr>
                ` : ''}
                ${order.IsTaxable && !order.IsTaxIncluded && order.TaxAmount > 0 ? `
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left;"><strong>מע"מ (${order.TaxRate || 18}%):</strong></td>
                  <td>+${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(order.TaxAmount)}</td>
                </tr>
                ` : ''}
                ${order.RoundingEnabled && order.RoundingAdjustment !== 0 ? `
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left; color: #666; font-size: 0.9em;">סה"כ לפני עיגול:</td>
                  <td style="color: #666; font-size: 0.9em;">${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format((order.TotalAmount || 0) - (order.RoundingAdjustment || 0))}</td>
                </tr>
                <tr>
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left; color: #1976d2; font-size: 0.9em;">עיגול:</td>
                  <td style="color: #1976d2; font-size: 0.9em;">${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(order.RoundingAdjustment)}</td>
                </tr>
                ` : ''}
                <tr class="total-row">
                  <td colspan="${orderItemFields.includes("Status") ? '5' : '4'}" style="text-align: left;"><strong>סה"כ לתשלום:</strong></td>
                  <td>${new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(order.TotalAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          ${orderFields.includes("PaidAmount") ? `
            <div class="section">
              <h2>תשלומים</h2>
              <p>
                <strong>סכום ששולם:</strong> 
                ${new Intl.NumberFormat('he-IL', {
                  style: 'currency',
                  currency: 'ILS',
                }).format(order.PaidAmount || 0)}
              </p>
              <p>
                <strong>יתרה לתשלום:</strong> 
                ${new Intl.NumberFormat('he-IL', {
                  style: 'currency',
                  currency: 'ILS',
                }).format((order.TotalAmount || 0) - (order.PaidAmount || 0))}
              </p>
              ${order.payments && order.payments.length > 0 ? `
                <table>
                  <thead>
                    <tr>
                      <th>תאריך</th>
                      <th>אמצעי תשלום</th>
                      <th>פרטים</th>
                      <th>סכום</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${order.payments.map(payment => {
                      let paymentMethodDisplay = '';
                      switch (payment.PaymentMethod) {
                        case 'cash': paymentMethodDisplay = 'מזומן'; break;
                        case 'credit_card': paymentMethodDisplay = 'כרטיס אשראי'; break;
                        case 'bank_transfer': paymentMethodDisplay = 'העברה בנקאית'; break;
                        case 'check': paymentMethodDisplay = "צ'ק"; break;
                        default: paymentMethodDisplay = payment.PaymentMethod;
                      }
                      
                      let details = '';
                      if (payment.PaymentMethod === 'check' && payment.CheckNumber) {
                        details = `מספר צ'ק: ${payment.CheckNumber}`;
                        if (payment.CheckAccountNumber) {
                          details += ` | חשבון: ${payment.CheckAccountNumber}`;
                        }
                      } else if (payment.PaymentMethod === 'credit_card' && payment.CardLastFour) {
                        details = `כרטיס: ${payment.CardLastFour}`;
                      } else if (payment.PaymentMethod === 'bank_transfer' && payment.BankName) {
                        details = `בנק: ${payment.BankName}`;
                      }

                      return `
                        <tr>
                          <td>${new Date(payment.PaymentDate).toLocaleDateString('he-IL')}</td>
                          <td>${paymentMethodDisplay}</td>
                          <td>${details}</td>
                          <td>${new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(payment.Amount)}</td>
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              ` : '<p>אין תשלומים</p>'}
            </div>
          ` : ''}
          
          ${order.Notes ? `
            <div class="section">
              <h2>הערות</h2>
              <p>${order.Notes}</p>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>הודפס בתאריך: ${new Date().toLocaleDateString('he-IL')}</p>
          </div>
        </body>
        </html>
      `;
    openPrintWindow(htmlContent);
  };

  // Print order without prices function
  const handlePrintOrderWithoutPrices = () => {
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl">
        <head>
          <title>${orderLabel} מס' ${order.OrderNumber}</title>
          <meta charset="utf-8" />
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              direction: rtl;
            }
            h1, h2 {
              margin-bottom: 10px;
            }
            .header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 10px;
            }
            .section {
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: right;
            }
            th {
              background-color: #f2f2f2;
            }
            .item-status {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 0.9em;
            }
            .item-status.supplied {
              background-color: #e8f5e9;
              color: #4caf50;
            }
            .item-status.not-supplied {
              background-color: #ffebee;
              color: #f44336;
            }
            .item-barcode {
              margin-top: 6px;
              line-height: 0;
            }
            .item-barcode svg {
              display: block;
              max-width: 220px;
              height: auto;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 0.8em;
              color: #666;
            }
            @media print {
              .no-print {
                display: none;
              }
              button {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          ${generateBusinessHeader(businessProfile)}
          <div class="header">
            <div>
              <h1>${orderLabel} מס' ${order.OrderNumber}</h1>
              <p>תאריך: ${new Date(order.OrderDate).toLocaleDateString('he-IL')}</p>
              ${orderFields.includes("DueDate") && order.DueDate ? `<p>תאריך אספקה: ${new Date(order.DueDate).toLocaleDateString('he-IL')}</p>` : ''}
            </div>
            <div>
              ${orderFields.includes("Status") ? `<p><strong>סטטוס:</strong> ${getStatusName(order.Status)}</p>` : ''}
              ${order.InvoiceNumber ? `<p><strong>מספר חשבונית:</strong> ${order.InvoiceNumber}</p>` : ''}
            </div>
          </div>
          
          <div class="section">
            <h2>פרטי לקוח</h2>
            <p><strong>שם:</strong> ${order.ClientName}</p>
            <p><strong>מספר לקוח:</strong> ${order.ClientBusinessID}</p>
          </div>
          
          <div class="section">
            <h2>${productPluralLabel}</h2>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>שם ${productLabel}</th>
                  <th>כמות</th>
                  ${orderItemFields.includes("Status") ? '<th>סטטוס</th>' : ''}
                </tr>
              </thead>
              <tbody>
                ${order.items.map((item, index) => {
                  const printBarcode = orderPrintItemFields.includes('Barcode');
                  const barcodeSvg = (printBarcode && item.ProductBarcode)
                    ? generateBarcodeSVGString(item.ProductBarcode, {
                        width: 1.4, height: 28, fontSize: 9, textMargin: 1, margin: 2,
                        preferredSymbology: barcodePrintSymbology,
                      })
                    : '';
                  return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>
                      ${item.ProductName}
                      ${barcodeSvg ? `<div class="item-barcode">${barcodeSvg}</div>` : ''}
                    </td>
                    <td>${item.Quantity}</td>
                    ${orderItemFields.includes("Status") ? `
                      <td>
                        <span class="item-status ${item.Status === 'supplied' ? 'supplied' : 'not-supplied'}">
                          ${item.StatusDisplay || (item.Status === 'supplied' ? 'סופק' : 'לא סופק')}
                        </span>
                      </td>
                    ` : ''}
                  </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>

          ${order.Notes ? `
            <div class="section">
              <h2>הערות</h2>
              <p>${order.Notes}</p>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>הודפס בתאריך: ${new Date().toLocaleDateString('he-IL')}</p>
          </div>
        </body>
        </html>
      `;
    openPrintWindow(htmlContent);
  };

  // Get payment method name
  const getPaymentMethodName = (method) => {
    switch (method) {
      case 'cash': return 'מזומן';
      case 'credit_card': return 'כרטיס אשראי';
      case 'bank_transfer': return 'העברה בנקאית';
      case 'check': return "צ'ק";
      default: return method;
    }
  };
  
  if (isLoading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (isError) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          {error?.response?.data?.error || `שגיאה בטעינת פרטי ה${orderLabel}`}
        </Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backPath)}
          >
            {backLabel}
          </Button>
        </Box>
      </Container>
    );
  }
  
  // Calculate payment status
  const totalAmount = parseFloat(order.TotalAmount) || 0;
  const paidAmount = parseFloat(order.PaidAmount) || 0;
  const remainingAmount = totalAmount - paidAmount;
  const paymentStatus = 
    paidAmount >= totalAmount ? 'fully_paid' : 
    paidAmount > 0 ? 'partial_paid' : 'unpaid';
  
  // Get payment status details
  const getPaymentStatusDetails = () => {
    switch (paymentStatus) {
      case 'fully_paid':
        return { 
          label: 'שולם במלואו',
          color: { bg: '#e8f5e9', color: '#4caf50' }
        };
      case 'partial_paid':
        return { 
          label: 'שולם חלקית',
          color: { bg: '#e1f5fe', color: '#0288d1' }
        };
      default:
        return { 
          label: 'לא שולם',
          color: { bg: '#ffebee', color: '#f44336' }
        };
    }
  };
  
  const paymentStatusDetails = getPaymentStatusDetails();
  
  const InventoryWarningAlert = ({ product, onOpenProductPage }) => {
    if (!product) return null;
    
    return (
      <Alert 
        severity="warning" 
        icon={<WarningIcon />}
        sx={{ mt: 2, mb: 2 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            אין מלאי זמין עבור {productLabel} זה
          </Typography>
          <Typography variant="body2">
            לא ניתן להוסיף {productLabel} זה להזמנה מכיוון שאין מלאי זמין באף מחסן.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={onOpenProductPage}
              sx={{ direction: 'ltr' }}
            >
              עדכון מלאי
            </Button>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: 'warning.dark',
                backgroundColor: 'warning.lighter',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main'
              }}
            >
              אחרי העדכון, בחר מחדש את ה{productLabel}
            </Typography>
          </Box>
        </Box>
      </Alert>
    );
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container maxWidth="lg" sx={{ my: { xs: 1, sm: 4 }, px: { xs: 1, sm: 2 } }}>
            {/* Success message */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            )}
            
            {/* Error message */}
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </Alert>
            )}
            
            {/* Header with actions */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: { xs: 1.5, md: 2 },
              mb: 3,
            }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  wordBreak: 'break-word',
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                {orderLabel} מספר {order.OrderNumber}
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                flexShrink: 0,
                '& > .MuiButton-root': {
                  flex: { xs: '1 1 auto', md: '0 0 auto' },
                  minWidth: 0,
                },
              }}>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintOrderWithoutPrices}
                  sx={{ direction: 'ltr' }}
                >
                  הדפס ללא מחירים
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintOrder}
                  sx={{ direction: 'ltr' }}
                >
                  הדפס מלא
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(backPath)}
                  sx={{ direction: 'ltr' }}
                >
                  {backLabel}
                </Button>
              </Box>
            </Box>
            
            {/* Global action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 3 }}>
              {/* Danger actions group */}
              <Box sx={{ display: 'flex', gap: 1 }}> 
                  {order.Status !== 'cancelled' && (
                    <Button
                      color='error'
                      variant='contained'
                      startIcon={<CancelIcon />}
                      onClick={handleCancelClick}
                      sx={{direction: 'ltr'}}
                    >
                      בטל {orderLabel}
                    </Button>
                  )}

                  {/* <Button
                    color="error"
                    variant='contained'
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                    sx={{direction: 'ltr'}}
                  >
                    מחק {orderLabel}
                  </Button> */}
              </Box>
            </Box>
            
            {/* Order details grid */}
            <Grid container spacing={3}>
              {/* Left column - Order details */}
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Card sx={{ borderRadius: 2, boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardHeader
                    title={
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                        פרטי {orderLabel}
                      </Typography>
                    }
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      py: 1.5,
                    }}
                    action={
                      order.Status !== 'cancelled' && orderFields.includes("Status") && (
                        <Tooltip title="עדכון סטטוס">
                          <IconButton 
                            color="inherit" 
                            onClick={handleStatusClick}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          לקוח:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {ClientAllowedRoles.includes(role) ? (
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => navigate(`/client/${order.ClientBusinessID}`)}
                              sx={{ p: 0, textDecoration: 'underline', fontWeight: 'bold' }}
                            >
                              {order.ClientName}
                            </Button>
                          ) : (
                            <Typography 
                              component="span"
                              sx={{ 
                                fontWeight: 'bold',
                                color: 'text.primary'
                              }}
                            >
                              {order.ClientName}
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          מס' לקוח/ת.ז:
                        </Typography>
                        <Typography variant="body1">
                          {order.ClientBusinessID}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            תאריך {orderLabel}:
                          </Typography>
                          {/* NEW: Add edit icon for order date */}
                          {order.Status !== 'cancelled' && order.Status !== 'completed' && (
                            <Tooltip title="ערוך תאריך הזמנה">
                              <IconButton 
                                color="primary" 
                                onClick={handleOrderDateClick}
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body1">
                          {new Date(order.OrderDate).toLocaleDateString('he-IL')}
                        </Typography>
                      </Grid>

                      { orderFields.includes("DueDate") &&
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            תאריך אספקה:
                          </Typography>
                          {order.Status !== 'cancelled' && order.Status !== 'completed' && (
                            <Tooltip title="ערוך תאריך אספקה">
                              <IconButton 
                                color="primary" 
                                onClick={handleDueDateClick}
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body1">
                          {order.DueDate ? new Date(order.DueDate).toLocaleDateString('he-IL') : 'לא הוגדר'}
                        </Typography>
                      </Grid>
                      }

                      { orderFields.includes("Status") &&
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          סטטוס:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getStatusName(order.Status)}
                            sx={{
                              backgroundColor: getStatusColor(order.Status).bg,
                              color: getStatusColor(order.Status).color,
                              borderColor: getStatusColor(order.Status).color,
                              fontWeight: 'medium',
                            }}
                            variant="outlined"
                            size="small"
                          />  
                          {order.Status !== 'cancelled' && (
                            <Tooltip title="ערוך סטטוס">
                              <IconButton 
                                color="primary" 
                                onClick={handleStatusClick}
                                size="small"
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                      }
                      {order.CompletionDate && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            תאריך סיום:
                          </Typography>
                          <Typography variant="body1">
                            {new Date(order.CompletionDate).toLocaleDateString('he-IL')}
                          </Typography>
                        </Grid>
                      )}
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          מספר חשבונית:
                        </Typography>
                        <Typography variant="body1">
                          {order.InvoiceNumber || 'לא הוגדר'}
                        </Typography>
                      </Grid>
                      
                      {/* Display any extra fields */}
                      {order.extra_fields && Object.entries(order.extra_fields).map(([key, value]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {key}:
                          </Typography>
                          <Typography variant="body1">
                            {value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Right column - Financial details */}
              {orderFields.includes("PaidAmount") && 
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Card sx={{ borderRadius: 2, boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardHeader
                    title={
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                        פרטי תשלום
                      </Typography>
                    }
                    sx={{
                      backgroundColor: 'secondary.main',
                      color: 'white',
                      py: 1.5,
                    }}
                    action={
                      order.Status !== 'cancelled'  && remainingAmount > 0 && (
                        <Tooltip title="הוסף תשלום">
                          <IconButton 
                            color="inherit" 
                            onClick={handlePaymentClick}
                            size="small"
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      {order.Subtotal !== undefined && order.Subtotal !== order.TotalAmount && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              סכום לפני הנחה:
                            </Typography>
                            <Typography variant="body1">
                              {new Intl.NumberFormat('he-IL', {
                                style: 'currency',
                                currency: 'ILS',
                              }).format(order.Subtotal || 0)}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              הנחה:
                            </Typography>
                            <Typography variant="body1">
                              {order.DiscountPercentage > 0 && order.DiscountAmount > 0 ? (
                                // Both percentage and amount exist
                                <>
                                  {order.DiscountPercentage}% (
                                  {new Intl.NumberFormat('he-IL', {
                                    style: 'currency',
                                    currency: 'ILS',
                                  }).format(parseFloat(order.DiscountAmount))})
                                </>
                              ) : order.DiscountPercentage > 0 ? (
                                // Only percentage exists
                                `${order.DiscountPercentage}%`
                              ) : order.DiscountAmount > 0 ? (
                                // Only amount exists
                                new Intl.NumberFormat('he-IL', {
                                  style: 'currency',
                                  currency: 'ILS',
                                }).format(parseFloat(order.DiscountAmount))
                              ) : (
                                // No discount
                                'אין'
                              )}
                            </Typography>
                          </Grid>
                          
                          {order.DiscountReason && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" color="text.secondary">
                                סיבת הנחה:
                              </Typography>
                              <Typography variant="body1">
                                {order.DiscountReason}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Tax breakdown */}
                      {order.IsTaxable && order.TaxAmount > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {order.IsTaxIncluded ? `מע"מ כלול (${order.TaxRate || 18}%):` : `מע"מ (${order.TaxRate || 18}%):`}
                          </Typography>
                          <Typography variant="body1">
                            {order.IsTaxIncluded ? '' : '+'}{new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS',
                            }).format(order.TaxAmount || 0)}
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          סכום כולל:
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(order.TotalAmount || 0)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          סכום ששולם:
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color={paidAmount >= totalAmount ? 'success.main' : 'inherit'}>
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(order.PaidAmount || 0)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          יתרה לתשלום:
                        </Typography>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold" 
                          color={remainingAmount > 0 ? 'error.main' : 'success.main'}
                        >
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(remainingAmount)}
                        </Typography>
                      </Grid>
                      
                      { orderFields.includes("Status") && 
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          מצב תשלום:
                        </Typography>
                        <Chip
                          label={paymentStatusDetails.label}
                          sx={{
                            backgroundColor: paymentStatusDetails.color.bg,
                            color: paymentStatusDetails.color.color,
                            borderColor: paymentStatusDetails.color.color,
                            fontWeight: 'medium',
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      }
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              }
            </Grid>

            {/* Order Items Section */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: 4, 
              mb: 2
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                פריטי {orderLabel}
              </Typography>
              
              { order.Status !== 'cancelled' && order.Status !== 'completed' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddProductClick}
                  size="small"
                  sx={{direction: 'ltr'}}
                >
                  הוסף {productLabel}
                </Button>
              )}
            </Box>
            
            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, mt: 2, width: '100%' }}>
              <Table sx={{ width: '100%' }}>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell align="center" width="50px">מס'</TableCell>
                    <TableCell align="center" > שם {productLabel}</TableCell>
                    <TableCell align="center">כמות</TableCell>
                    {storageEnabled && <TableCell align="center">מחסן</TableCell>}
                    <TableCell align="right">מחיר יחידה</TableCell>
                    <TableCell align="right">סה"כ</TableCell>
                    {orderItemFields.includes("Status") && <TableCell align="center">סטטוס</TableCell>}
                    { order.Status !== 'cancelled' && order.Status !== 'completed' && (
                      <TableCell align="center" width="120px">פעולות</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items && order.items.map((item, index) => (
                    <TableRow key={item.OrderItemID} hover>
                      <TableCell align="center" width="50px">{index + 1}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => {
                            const productId = item.ProductID || item.Product;
                            if (productId) {
                              window.open(`/product/${productId}`, '_blank');
                            }
                          }}
                          sx={{ 
                            p: 0, 
                            textDecoration: 'underline',
                            fontWeight: 'medium',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            minWidth: 'auto',
                            fontSize: 'inherit',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: 'transparent'
                            }
                          }}
                        >
                          {item.ProductName}
                        </Button>
                      </TableCell>
                      <TableCell align="center">{item.Quantity}</TableCell>
                      {storageEnabled && (
                        <TableCell align="center">
                          {item.StorageName || 'ברירת מחדל'}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {item.PriceOverridden && item.OriginalPrice ? (
                          <Box sx={{ display:'flex', alignItems:'center', justifyContent: 'flex-end', direction: 'ltr' }}>
                            <Typography 
                              variant="body2" 
                              sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}
                            >
                              {new Intl.NumberFormat('he-IL', {
                                style: 'currency',
                                currency: 'ILS',
                              }).format(item.OriginalPrice)}
                            </Typography>
                            <Typography>
                              {new Intl.NumberFormat('he-IL', {
                                style: 'currency',
                                currency: 'ILS',
                              }).format(item.UnitPrice)}
                            </Typography>
                          </Box>
                        ) : (
                          new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(item.UnitPrice)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(item.Subtotal)}
                      </TableCell>
                      {orderItemFields.includes("Status") && <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Chip
                            label={item.StatusDisplay || (item.Status === 'supplied' ? 'סופק' : 'לא סופק')}
                            sx={{
                              backgroundColor: getItemStatusColor(item.Status).bg,
                              color: getItemStatusColor(item.Status).color,
                              borderColor: getItemStatusColor(item.Status).color,
                              fontWeight: 'medium',
                            }}
                            variant="outlined"
                            size="small"
                          />
                          {order.Status !== 'cancelled' && order.Status !== 'completed' && (
                            <Tooltip title="ערוך סטטוס">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleItemStatusClick(item)}
                                sx={{ ml: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      }
                      {order.Status !== 'cancelled' && order.Status !== 'completed' && (
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <Tooltip title={`ערוך ${productLabel}`}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditProduct(item)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`מחק ${productLabel}`}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteProduct(item.OrderItemID)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  
                  {/* Subtotal Row */}
                  {order.Subtotal !== undefined && order.Subtotal !== order.TotalAmount && (
                    <TableRow>
                      <TableCell colSpan={storageEnabled ? 5 : 4}  align="right" sx={{ fontWeight: 'bold' }}>
                        סה"כ לפני הנחה:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(order.Subtotal || 0)}
                      </TableCell>
                      {order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                    </TableRow>
                  )}
                  
                  {/* Discount Row */}
                  {(order.DiscountAmount > 0 || order.DiscountPercentage > 0) && (
                    <>
                      <TableRow>
                        <TableCell colSpan={storageEnabled ? 5 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                          הנחה{order.DiscountPercentage > 0 ? ` (${order.DiscountPercentage}%)` : ''}:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          -{new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(order.DiscountAmount || 0)}
                        </TableCell>
                        { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                      </TableRow>

                      {/* Net Amount after Discount Row */}
                      <TableRow>
                        <TableCell colSpan={storageEnabled ? 5 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                          סה"כ אחרי הנחה:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format((order.Subtotal || 0) - (order.DiscountAmount || 0))}
                        </TableCell>
                        { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                      </TableRow>
                    </>
                  )}

                  {/* Tax Row - Included */}
                  {order.IsTaxable && order.IsTaxIncluded && order.TaxAmount > 0 && (
                    <TableRow>
                      <TableCell colSpan={storageEnabled ? 5 : 4} align="right">
                        <Typography variant="body2" color="text.secondary">
                          מזה מע"מ ({order.TaxRate || 18}%):
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(order.TaxAmount || 0)}
                        </Typography>
                      </TableCell>
                      { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                    </TableRow>
                  )}

                  {/* Tax Row - Excluded */}
                  {order.IsTaxable && !order.IsTaxIncluded && order.TaxAmount > 0 && (
                    <TableRow>
                      <TableCell colSpan={storageEnabled ? 5 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                        מע"מ ({order.TaxRate || 18}%):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        +{new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(order.TaxAmount || 0)}
                      </TableCell>
                      { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                    </TableRow>
                  )}

                  {/* Rounding Rows - Only show if RoundingEnabled AND adjustment is not zero */}
                  {order.RoundingEnabled && order.RoundingAdjustment !== 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={storageEnabled ? 5 : 4} align="right">
                          <Typography variant="body2" color="text.secondary">
                            סה"כ לפני עיגול:
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS',
                            }).format((order.TotalAmount || 0) - (order.RoundingAdjustment || 0))}
                          </Typography>
                        </TableCell>
                        { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={storageEnabled ? 5 : 4} align="right">
                          <Typography variant="body2" color="primary.main">
                            עיגול:
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="primary.main">
                            {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS',
                            }).format(order.RoundingAdjustment || 0)}
                          </Typography>
                        </TableCell>
                        { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                      </TableRow>
                    </>
                  )}

                  {/* Total Row */}
                  <TableRow>
                    <TableCell colSpan={storageEnabled ? 5 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                      סה"כ לתשלום:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(order.TotalAmount || 0)}
                    </TableCell>
                    { order.Status !== 'cancelled' && order.Status !== 'completed' && <TableCell />}
                  </TableRow>

                  {/* Rounding Checkbox - Only show if order is not cancelled or completed */}
                  {order.Status !== 'cancelled' && order.Status !== 'completed' && (
                    <TableRow>
                      <TableCell colSpan={storageEnabled ? 6 : 5} align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mt: 1 }}>
                          {order.PaidAmount >= order.TotalAmount && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                              (לא ניתן לשנות - ההזמנה שולמה במלואה)
                            </Typography>
                          )}
                          <Tooltip
                            title={
                              order.PaidAmount >= order.TotalAmount
                                ? "לא ניתן לשנות עיגול בהזמנה ששולמה במלואה"
                                : ""
                            }
                          >
                            <span>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={roundingEnabled}
                                    onChange={handleRoundingToggle}
                                    disabled={order.PaidAmount >= order.TotalAmount}
                                  />
                                }
                                label="עיגול"
                              />
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Profit Summary — managers only. CostPrice is stripped from
                the response for employees, so this block won't appear for
                them anyway, but we keep the role check as defense in depth. */}
            {role === 'manager' && order.items && order.items.some(item => item.ProductCostPrice != null) && (() => {
              const itemsWithCost = order.items.filter(item => item.ProductCostPrice != null);
              const totalCost = itemsWithCost.reduce((sum, item) => sum + (parseFloat(item.ProductCostPrice) * parseFloat(item.Quantity)), 0);
              const totalRevenue = itemsWithCost.reduce((sum, item) => sum + (parseFloat(item.UnitPrice) * parseFloat(item.Quantity)), 0);
              const totalProfit = totalRevenue - totalCost;
              const profitPercentage = totalCost > 0 ? ((totalProfit / totalCost) * 100) : 0;
              const formatCurrency = (val) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(val);
              return (
                <Paper sx={{ mt: 2, p: 2, backgroundColor: totalProfit >= 0 ? '#f1f8e9' : '#fce4ec', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      סיכום רווח:
                    </Typography>
                    <Typography variant="body2">
                      עלות: {formatCurrency(totalCost)}
                    </Typography>
                    <Typography variant="body2">
                      הכנסה: {formatCurrency(totalRevenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: totalProfit >= 0 ? 'success.main' : 'error.main' }}>
                      רווח: {formatCurrency(totalProfit)} ({profitPercentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Paper>
              );
            })()}

            {/* Notes Section - Updated to match ClientDetailPage */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              הערות
            </Typography>
            <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
              <CardContent sx={{ p: 2 }}>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ 
                      whiteSpace: 'pre-line', 
                      wordBreak: 'break-word',
                      maxHeight: notesExpanded ? 'none' : '150px',
                      overflowY: notesExpanded ? 'visible' : 'auto',
                      fontSize: '1.1rem'
                    }}
                  >
                    {notesValue || 'אין הערות'}
                  </Typography>
                  
                  {/* Success/Error messages in the card */}
                  {notesSuccessMsg && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {notesSuccessMsg}
                    </Alert>
                  )}
                  {notesErrorMsg && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {notesErrorMsg}
                    </Alert>
                  )}
                  
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                    }}
                  >
                    <IconButton
                      size="medium"
                      onClick={() => toggleExpandedNotesDialog(false)}
                      sx={{ color: 'primary.main' }}
                    >
                      <FullscreenIcon />
                    </IconButton>
                      <Button
                        variant="contained"
                        onClick={() => toggleExpandedNotesDialog(true)}
                      >
                        עריכה
                      </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            {/* Expanded Notes Dialog */}
            <MobileDialog
              open={expandedNotesDialog}
              onClose={(event, reason) => {
                // Prevent close on backdrop click if we're editing:
                if (reason !== 'backdropClick' || !editingNotes) {
                  setExpandedNotesDialog(false);
                  setEditingNotes(false);
                }
              }}
              PaperProps={{ 
                sx: { 
                  width: '80vw',
                  height: '75vh',
                  maxWidth: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                } 
              }}
            >
              <DialogTitle
                sx={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  p: 2,
                  flexShrink: 0,
                }}
              >
                הערות
              </DialogTitle>
              
              <DialogContent
                dividers
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  position: 'relative',
                  p: 0,
                }}
              >
                {/* Main Content Container */}
                <Box sx={{
                  flex: 1,
                  overflow: 'hidden',
                  padding: '16px',
                  paddingRight: '24px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {!editingNotes ? (
                    <Box sx={{
                      flex: 1,
                      overflow: 'auto',
                    }}>
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-line',
                          wordBreak: 'break-word',
                          fontSize: '1.5rem',
                          lineHeight: '1.5',
                        }}
                      >
                        {notesValue || 'אין הערות'}
                      </Typography>
                    </Box>) : (
                    <Box sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'auto'
                    }}>
                      <TextField
                        label="ערוך הערות"
                        multiline
                        fullWidth
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        variant="outlined"
                        ref={notesTextFieldRef}
                        sx={{
                          flex: 1,
                          '& .MuiInputBase-root': {
                            fontSize: '1.5rem',
                            height: '100%',
                            alignItems: 'flex-start',
                            overflow: 'auto',
                            paddingRight: '8px',
                          },
                          
                        }}
                        InputProps={{
                          style: { 
                            height: '100%',
                            boxSizing: 'border-box',
                          }
                        }}
                      />
                      
                      {/* Error/Success Messages */}
                      <Box sx={{ mt: 2, flexShrink: 0 }}>
                        {notesErrorMsg && <Alert severity="error">{notesErrorMsg}</Alert>}
                        {notesSuccessMsg && <Alert severity="success">{notesSuccessMsg}</Alert>}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Sticky Bottom Buttons */}
                <Box sx={{
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'background.paper',
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  zIndex: 2,
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-start',
                }}>
                  {editingNotes ? (
                    <>
                      <Button
                        onClick={() => {
                          setExpandedNotesDialog(false);
                          setEditingNotes(false);
                          setNotesErrorMsg('');
                          setNotesSuccessMsg('');
                          setNotesValue(order.Notes || '');
                        }}
                        variant="outlined"
                        sx={{ fontSize: '1.2rem', minWidth: '120px' }}
                      >
                        ביטול
                      </Button>
                      <Button
                        onClick={handleSaveNotes}
                        variant="contained"
                        disabled={notesLoadingState}
                        sx={{ fontSize: '1.2rem', minWidth: '120px' }}
                      >
                        {notesLoadingState ? 'שומר...' : 'שמור'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <IconButton
                        onClick={() => setExpandedNotesDialog(false)}
                        sx={{ color: 'primary.main' }}
                      >
                        <FullscreenExitIcon fontSize="large" />
                      </IconButton>

                      <Button
                        variant="contained"
                        onClick={() => setEditingNotes(true)}
                        sx={{ fontSize: '1.2rem', minWidth: '120px' }}
                      >
                        עריכה
                      </Button>
                    </>
                  )}
                </Box>
              </DialogContent>
            </MobileDialog>
            
            {/* Payments Section */}
            { orderFields.includes("PaidAmount") &&
             <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">תשלומים</Typography>
              
              { order.Status !== 'cancelled' && remainingAmount > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={handlePaymentClick}
                  size="small"
                  sx={{
                    direction: 'ltr'
                  }}
                >
                  הוסף תשלום
                </Button>
              )}
            </Box>
            
            {order.payments && order.payments.length > 0 ? (
              <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, width: '100%' }}>
                <Table sx={{ width: '100%' }}>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell>תאריך</TableCell>
                      <TableCell>אמצעי תשלום</TableCell>
                      <TableCell align="center">פרטים</TableCell>
                      <TableCell align="center">הערות</TableCell>
                      <TableCell align="right">סכום</TableCell>
                      { order.Status !== 'cancelled' && order.Status !== 'completed' && (
                        <TableCell align="center" width="100px" sx={{ minWidth: '100px' }}>פעולות</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.payments.map((payment) => {
                      // Get payment details based on method
                      let details = '';
                      
                      if (payment.PaymentMethod === 'check' && payment.CheckNumber) {
                        details = `מספר צ'ק: ${payment.CheckNumber}`;
                        if (payment.CheckBankName) {
                          details += `, בנק: ${payment.CheckBankName}`;
                        }
                        if (payment.CheckAccountNumber) {
                          details += `, חשבון: ${payment.CheckAccountNumber}`;
                        }
                        if (payment.CheckDate) {
                          details += `, לתאריך: ${new Date(payment.CheckDate).toLocaleDateString('he-IL')}`;
                        }
                      } else if (payment.PaymentMethod === 'credit_card') {
                        if (payment.CardLastFour) {
                          details = `מספר: ${payment.CardLastFour}`;
                          if (payment.CardType) {
                            details += ` (${payment.CardType})`;
                          }
                        }
                      } else if (payment.PaymentMethod === 'bank_transfer') {
                        if (payment.BankName) {
                          details = `בנק: ${payment.BankName}`;
                          if (payment.BankAccountLast4) {
                            details += `, חשבון: ****${payment.BankAccountLast4}`;
                          }
                          if (payment.TransferDate) {
                            details += `, תאריך: ${new Date(payment.TransferDate).toLocaleDateString('he-IL')}`;
                          }
                        }
                      }
                      
                      return (
                        <TableRow key={payment.PaymentID} hover>
                          <TableCell>
                            {new Date(payment.PaymentDate).toLocaleDateString('he-IL')}
                          </TableCell>
                          <TableCell>{getPaymentMethodName(payment.PaymentMethod)}</TableCell>
                          <TableCell align="center">{details}</TableCell>
                          <TableCell align="center">{payment.Notes}</TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS',
                            }).format(payment.Amount)}
                          </TableCell>
                          { order.Status !== 'cancelled' && order.Status !== 'completed' && (
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                <Tooltip title="ערוך תשלום">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleEditPayment(payment)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="מחק תשלום">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeletePayment(payment.PaymentID)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    
                    {/* Total Paid Row */}
                    <TableRow>
                      <TableCell colSpan={order.Status !== 'cancelled' && order.Status !== 'completed' ? 4 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                        סה"כ ששולם:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: paidAmount >= totalAmount ? 'success.main' : 'inherit' }}>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(order.PaidAmount || 0)}
                      </TableCell>
                      { order.Status !== 'cancelled' && order.Status !== 'completed' && (
                        <TableCell align="center" />
                      )}
                    </TableRow>
                    
                    {/* Remaining Amount Row */}
                    {remainingAmount > 0 && (
                      <TableRow>
                        <TableCell colSpan={order.Status !== 'cancelled' && order.Status !== 'completed' ? 4 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                          יתרה לתשלום:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(remainingAmount)}
                        </TableCell>
                        { order.Status !== 'cancelled' && order.Status !== 'completed' && (
                          <TableCell align="center" />
                        )}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                אין תשלומים ל{orderLabel} זו
              </Alert>
            )}
            </>
            }

            {/* Status Update Dialog */}
            <MobileDialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>עדכון סטטוס {orderLabel}</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <TextField
                  select
                  label="סטטוס"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  fullWidth
                  margin="normal"
                >
                  {(orderFieldsSchema.Status?.options || [
                    { value: 'pending', label: 'בתהליך' },
                    { value: 'processing', label: 'בטיפול' },
                    { value: 'completed', label: 'הושלם' },
                    { value: 'cancelled', label: 'בוטל' },
                  ]).filter(option => option.value !== 'cancelled').map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setStatusDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleStatusUpdate} 
                  variant="contained" 
                  color="primary"
                  disabled={updateStatusMutation.isLoading}
                >
                  {updateStatusMutation.isLoading ? 'מעדכן...' : 'עדכן'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Due Date Dialog */}
            <MobileDialog open={dueDateDialogOpen} onClose={() => setDueDateDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>עדכון תאריך אספקה</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Box sx={{ mt: 1 }}>
                  <DatePicker
                    label="תאריך אספקה"
                    value={newDueDate}
                    onChange={(newValue) => setNewDueDate(newValue)}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        margin: 'normal' 
                      } 
                    }}
                    sx={{direction: 'ltr'}}
                  />
                  <FormHelperText>
                    השאר ריק אם אין תאריך אספקה מתוכנן
                  </FormHelperText>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDueDateDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleDueDateUpdate} 
                  variant="contained" 
                  color="primary"
                  disabled={updateDueDateMutation.isLoading}
                >
                  {updateDueDateMutation.isLoading ? 'מעדכן...' : 'עדכן'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* NEW: Order Date Dialog */}
            <MobileDialog open={orderDateDialogOpen} onClose={() => setOrderDateDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>עדכון תאריך הזמנה</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Box sx={{ mt: 1 }}>
                  <DatePicker
                    label="תאריך הזמנה"
                    value={newOrderDate}
                    onChange={(newValue) => setNewOrderDate(newValue)}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        margin: 'normal' 
                      } 
                    }}
                    sx={{direction: 'ltr'}}
                  />
                  <FormHelperText>
                    בחר תאריך הזמנה
                  </FormHelperText>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOrderDateDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleOrderDateUpdate} 
                  variant="contained" 
                  color="primary"
                  disabled={updateOrderDateMutation.isLoading}
                >
                  {updateOrderDateMutation.isLoading ? 'מעדכן...' : 'עדכן'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Delete Confirmation Dialog */}
            <MobileDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
              <DialogTitle>מחיקת {orderLabel}</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Typography>
                  האם אתה בטוח שברצונך למחוק את ה{orderLabel}? פעולה זו אינה ניתנת לביטול.
                </Typography>
                {order.payments && order.payments.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    שים לב: {orderLabel} זו כבר כוללת תשלומים. מחיקת ה{orderLabel} עלולה להשפיע על מאזן הלקוח.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleDeleteConfirm} 
                  color="error" 
                  variant="contained"
                  disabled={deleteOrderMutation.isLoading}
                >
                  {deleteOrderMutation.isLoading ? 'מוחק...' : 'מחק'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Cancel Order Dialog */}
            <MobileDialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>ביטול {orderLabel}</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Typography paragraph>
                  האם אתה בטוח שברצונך לבטל את ה{orderLabel}? פעולה זו תשנה את סטטוס ה{orderLabel} ל"בוטל".
                </Typography>
                
                {/* <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  טיפול בתשלומים:
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={refundPayments}
                      onChange={(e) => setRefundPayments(e.target.checked)}
                      id="refund-checkbox"
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <Typography 
                      component="label" 
                      htmlFor="refund-checkbox" 
                      sx={{ ml: 1, cursor: 'pointer' }}
                    >
                      {paidAmount > 0 
                        ? `החזר תשלומים קיימים (${new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(paidAmount)})`
                        : 'החזר תשלומים (אם יהיו)'
                      }
                    </Typography>
                    <Tooltip title="אם מסומן, כל התשלומים יסומנו כמוחזרים וסכום ההזמנה יוחזר ליתרת הלקוח">
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </FormControl> */}
                
                <TextField
                  label="הערות ביטול"
                  multiline
                  rows={3}
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCancelDialogOpen(false)}>סגור</Button>
                <Button 
                  onClick={handleCancelConfirm} 
                  color="error" 
                  variant="contained"
                  disabled={cancelOrderMutation.isLoading}
                >
                  {cancelOrderMutation.isLoading ? 'מבטל...' : `בטל ${orderLabel}`}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Add Payment Dialog */}
            <MobileDialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>הוספת תשלום</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Box sx={{ mt: 1 }}>
                  <TextField
                    label={`סכום (יתרה: ${new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(remainingAmount)})`}
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    fullWidth
                    onFocus={(e) => e.target.select()} 
                    margin="normal"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: <InputAdornment position="start">₪</InputAdornment>
                    }}
                    required
                    error={!!paymentAmountError}
                    helperText={paymentAmountError}
                  />
                  
                  <TextField
                    select
                    label="אמצעי תשלום"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    fullWidth
                    margin="normal"
                    required
                  >
                    <MenuItem value="cash">מזומן</MenuItem>
                    <MenuItem value="credit_card">כרטיס אשראי</MenuItem>
                    <MenuItem value="bank_transfer">העברה בנקאית</MenuItem>
                    <MenuItem value="check">צ'ק</MenuItem>
                    <MenuItem value="other">אחר</MenuItem>
                  </TextField>
                  
                  {/* Payment method specific fields */}
                  {paymentMethod === 'check' && (
                    <>
                      <TextField
                        label="מספר צ'ק"
                        value={checkNumber}
                        onChange={(e) => setCheckNumber(e.target.value)}
                        fullWidth
                        margin="normal"
                        error={!!checkError}
                        helperText={checkError}
                      />
                      
                      <TextField
                        label="בנק"
                        value={checkBankName}
                        onChange={(e) => setCheckBankName(e.target.value)}
                        fullWidth
                        margin="normal"
                      />

                      <TextField
                        label="מספר חשבון"
                        value={checkAccountNumber}
                        onChange={(e) => setCheckAccountNumber(e.target.value)}
                        fullWidth
                        margin="normal"
                      />

                      <DatePicker
                        label="תאריך צ'ק"
                        value={checkDate}
                        onChange={(newValue) => setCheckDate(newValue)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true, 
                            margin: 'normal' 
                          } 
                        }}
                        sx={{direction: 'ltr'}}
                      />
                    </>
                  )}
                  
                  {paymentMethod === 'credit_card' && (
                    <>
                      <TextField
                        label="4 ספרות אחרונות"
                        value={cardLastFour}
                        onChange={(e) => {
                          // Only allow up to 4 digits
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setCardLastFour(val);
                        }}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          inputProps: { 
                            maxLength: 4,
                            pattern: "[0-9]*"
                          }
                        }}
                      />
                      
                      <TextField
                        label="סוג כרטיס"
                        value={cardType}
                        onChange={(e) => setCardType(e.target.value)}
                        fullWidth
                        margin="normal"
                        select
                      >
                        <MenuItem value="">בחר סוג כרטיס</MenuItem>
                        <MenuItem value="Visa">Visa</MenuItem>
                        <MenuItem value="Mastercard">Mastercard</MenuItem>
                        <MenuItem value="American Express">American Express</MenuItem>
                        <MenuItem value="Diners">Diners</MenuItem>
                        <MenuItem value="IsraCard">ישראכרט</MenuItem>
                      </TextField>
                    </>
                  )}
                  
                  {paymentMethod === 'bank_transfer' && (
                    <>
                      <TextField
                        label="שם הבנק"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        fullWidth
                        margin="normal"
                      />
                      
                      <TextField
                        label="4 ספרות אחרונות של החשבון"
                        value={bankAccountLast4}
                        onChange={(e) => {
                          // Only allow up to 4 digits
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setBankAccountLast4(val);
                        }}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          inputProps: { 
                            maxLength: 4,
                            pattern: "[0-9]*"
                          }
                        }}
                      />
                      
                      <DatePicker
                        label="תאריך העברה"
                        value={transferDate}
                        onChange={(newValue) => setTransferDate(newValue)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true, 
                            margin: 'normal' 
                          }
                        }}
                        sx={{direction: 'ltr'}}
                      />
                    </>
                  )}
                  
                  <TextField
                    label="הערות"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPaymentDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleAddPayment} 
                  variant="contained" 
                  color="primary"
                  disabled={addPaymentMutation.isLoading}
                >
                  {addPaymentMutation.isLoading ? 'מוסיף תשלום...' : 'הוסף תשלום'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Edit Payment Dialog */}
            <MobileDialog open={editPaymentDialogOpen} onClose={() => setEditPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>עריכת תשלום</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Box sx={{ mt: 1 }}>
                  <TextField
                    label="סכום"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: <InputAdornment position="start">₪</InputAdornment>
                    }}
                    required
                    error={!!paymentAmountError}
                    helperText={paymentAmountError}
                  />
                  
                  <TextField
                    select
                    label="אמצעי תשלום"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    fullWidth
                    margin="normal"
                    required
                  >
                    <MenuItem value="cash">מזומן</MenuItem>
                    <MenuItem value="credit_card">כרטיס אשראי</MenuItem>
                    <MenuItem value="bank_transfer">העברה בנקאית</MenuItem>
                    <MenuItem value="check">צ'ק</MenuItem>
                    <MenuItem value="other">אחר</MenuItem>
                  </TextField>
                  
                  {/* Payment method specific fields - same as Add Payment Dialog */}
                  {paymentMethod === 'check' && (
                    <>
                      <TextField
                        label="מספר צ'ק"
                        value={checkNumber}
                        onChange={(e) => setCheckNumber(e.target.value)}
                        fullWidth
                        margin="normal"
                        error={!!checkError}
                        helperText={checkError}
                      />
                      
                      <TextField
                        label="בנק"
                        value={checkBankName}
                        onChange={(e) => setCheckBankName(e.target.value)}
                        fullWidth
                        margin="normal"
                      />

                      <TextField
                        label="מספר חשבון"
                        value={checkAccountNumber}
                        onChange={(e) => setCheckAccountNumber(e.target.value)}
                        fullWidth
                        margin="normal"
                      />

                      <DatePicker
                        label="תאריך צ'ק"
                        value={checkDate}
                        onChange={(newValue) => setCheckDate(newValue)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true, 
                            margin: 'normal' 
                          } 
                        }}
                        sx={{direction: 'ltr'}}
                      />
                    </>
                  )}
                  
                  {paymentMethod === 'credit_card' && (
                    <>
                      <TextField
                        label="4 ספרות אחרונות"
                        value={cardLastFour}
                        onChange={(e) => {
                          // Only allow up to 4 digits
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setCardLastFour(val);
                        }}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          inputProps: { 
                            maxLength: 4,
                            pattern: "[0-9]*"
                          }
                        }}
                      />
                      
                      <TextField
                        label="סוג כרטיס"
                        value={cardType}
                        onChange={(e) => setCardType(e.target.value)}
                        fullWidth
                        margin="normal"
                        select
                      >
                        <MenuItem value="">בחר סוג כרטיס</MenuItem>
                        <MenuItem value="Visa">Visa</MenuItem>
                        <MenuItem value="Mastercard">Mastercard</MenuItem>
                        <MenuItem value="American Express">American Express</MenuItem>
                        <MenuItem value="Diners">Diners</MenuItem>
                        <MenuItem value="IsraCard">ישראכרט</MenuItem>
                      </TextField>
                    </>
                  )}
                  
                  {paymentMethod === 'bank_transfer' && (
                    <>
                      <TextField
                        label="שם הבנק"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        fullWidth
                        margin="normal"
                      />
                      
                      <TextField
                        label="4 ספרות אחרונות של החשבון"
                        value={bankAccountLast4}
                        onChange={(e) => {
                          // Only allow up to 4 digits
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setBankAccountLast4(val);
                        }}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          inputProps: { 
                            maxLength: 4,
                            pattern: "[0-9]*"
                          }
                        }}
                      />
                      
                      <DatePicker
                        label="תאריך העברה"
                        value={transferDate}
                        onChange={(newValue) => setTransferDate(newValue)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true, 
                            margin: 'normal' 
                          } 
                        }}
                        sx={{direction: 'ltr'}}
                      />
                    </>
                  )}
                  
                  <TextField
                    label="הערות"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditPaymentDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleSavePaymentEdit} 
                  variant="contained" 
                  color="primary"
                  disabled={editPaymentMutation.isLoading}
                >
                  {editPaymentMutation.isLoading ? 'מעדכן תשלום...' : 'שמור שינויים'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Delete Payment Dialog */}
            <MobileDialog open={deletePaymentDialogOpen} onClose={() => setDeletePaymentDialogOpen(false)}>
              <DialogTitle>מחיקת תשלום</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Typography>
                  האם אתה בטוח שברצונך למחוק את התשלום הזה? הפעולה אינה ניתנת לביטול.
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  מחיקת התשלום תשפיע על מאזן התשלומים ב{orderLabel} ועל יתרת החוב של הלקוח.
                </Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeletePaymentDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleConfirmDeletePayment} 
                  color="error" 
                  variant="contained"
                  disabled={deletePaymentMutation.isLoading}
                >
                  {deletePaymentMutation.isLoading ? 'מוחק...' : 'מחק תשלום'}
                </Button>
              </DialogActions>
            </MobileDialog>

            {/* Order Item Status Dialog */}
            <MobileDialog open={itemStatusDialogOpen} onClose={() => setItemStatusDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>עדכון סטטוס פריט</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                {editingItemStatus && (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      <strong>{productLabel}:</strong> {editingItemStatus.ProductName}
                    </Typography>
                    <TextField
                      select
                      label="סטטוס"
                      value={newItemStatus}
                      onChange={(e) => setNewItemStatus(e.target.value)}
                      fullWidth
                      margin="normal"
                    >
                      <MenuItem value="not_supplied">לא סופק</MenuItem>
                      <MenuItem value="supplied">סופק</MenuItem>
                    </TextField>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setItemStatusDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleItemStatusUpdate} 
                  variant="contained" 
                  color="primary"
                  disabled={updateItemStatusMutation.isLoading}
                >
                  {updateItemStatusMutation.isLoading ? 'מעדכן...' : 'עדכן'}
                </Button>
              </DialogActions>
            </MobileDialog> 
            
            {/* Delete Product Dialog */}
            <MobileDialog open={deleteProductDialogOpen} onClose={() => setDeleteProductDialogOpen(false)}>
              <DialogTitle>מחיקת {productLabel}</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Typography>
                  האם אתה בטוח שברצונך למחוק את ה{productLabel} הזה מה{orderLabel}?
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  מחיקת ה{productLabel} תשפיע על סכום ה{orderLabel} ועל יתרת החוב של הלקוח.
                </Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteProductDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleConfirmDeleteProduct} 
                  color="error" 
                  variant="contained"
                  disabled={deleteProductMutation.isLoading}
                >
                  {deleteProductMutation.isLoading ? 'מוחק...' : `מחק ${productLabel}`}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {/* Edit Product Dialog */}
            <MobileDialog open={productEditDialogOpen} onClose={handleCancelProductEdit} maxWidth="sm" fullWidth>
              <DialogTitle>עריכת {productLabel}</DialogTitle>
              <DialogContent>
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                <Box sx={{ mt: 2 }}>
                  {editingProductItem && (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {editingProductItem.ProductName}
                      </Typography>
                      
                      {/* Only show inventory info if storage is enabled AND we have the data */}
                      {storageEnabled && editingProductInventory && (
                        <Alert 
                          severity={editingProductInventory.availableInStorage > 0 ? "success" : "warning"} 
                          sx={{ 
                            mt: 2, 
                            mb: 2.5,
                            p: 1.5,
                            backgroundColor: editingProductInventory.availableInStorage > 0 
                              ? 'rgba(46, 125, 50, 0.08)' 
                              : 'rgba(237, 108, 2, 0.08)'
                          }}
                          icon={false}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              <strong>מחסן:</strong>
                            </Typography>
                            <Chip 
                              label={editingProductInventory.storageName}
                              size="small"
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: '0.9rem'
                              }}
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                              מלאי זמין:
                            </Typography>
                            <Typography 
                              variant="h6" 
                              component="span"
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: '1.3rem',
                                color: editingProductInventory.availableInStorage > 0 ? 'success.main' : 'warning.main'
                              }}
                            >
                              {editingProductInventory.availableInStorage}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              יחידות
                            </Typography>
                          </Box>
                          
                          {editingProductInventory.currentAllocated > 0 && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.95rem' }}
                              >
                                כמות נוכחית בהזמנה: <strong>{editingProductInventory.currentAllocated}</strong> יחידות
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleOpenAdjustInventory}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              עדכן מלאי
                            </Button>
                          </Box>
                        </Alert>
                      )}
                      
                      <Box sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        mt: 1,
                      }}>
                        <TextField
                          label="כמות"
                          type="number"
                          variant="outlined"
                          fullWidth
                          onFocus={(e) => e.target.select()}
                          value={productQuantity}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            const roundedValue = Math.round(value * 100) / 100;

                            // Only validate against inventory if storage is enabled
                            if (storageEnabled && editingProductInventory) {
                              if (roundedValue > editingProductInventory.availableQuantity) {
                                setErrorMessage(`כמות מקסימלית זמינה: ${editingProductInventory.availableQuantity}`);
                              } else {
                                setProductQuantity(roundedValue);
                                setErrorMessage('');
                              }
                            } else {
                              // Normal behavior without storage
                              setProductQuantity(roundedValue);
                            }
                          }}
                          InputProps={{
                            inputProps: {
                              min: 0.01,
                              step: 0.01,
                              // Only add max if storage is enabled
                              ...(storageEnabled && editingProductInventory && {
                                max: editingProductInventory.availableQuantity
                              })
                            }
                          }}
                          error={
                            storageEnabled &&
                            editingProductInventory &&
                            productQuantity > editingProductInventory.availableQuantity
                          }
                          helperText={
                            storageEnabled &&
                            editingProductInventory &&
                            productQuantity > editingProductInventory.availableQuantity
                              ? `חורג מהמלאי הזמין`
                              : ''
                          }
                        />
                        <TextField
                          label="מחיר יחידה"
                          type="number"
                          variant="outlined"
                          fullWidth
                          value={productUnitPrice}
                          onChange={(e) => setProductUnitPrice(e.target.value)}
                          InputProps={{
                            inputProps: { min: 0, step: 0.01 },
                            startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                          }}
                          error={
                            editingProductItem.ProductMinPrice != null &&
                            productUnitPrice !== '' &&
                            parseFloat(productUnitPrice) < parseFloat(editingProductItem.ProductMinPrice)
                          }
                          helperText={(() => {
                            // MinPrice floor takes priority — it's the business
                            // constraint representatives need to respect.
                            const minPrice = editingProductItem.ProductMinPrice;
                            if (minPrice != null && productUnitPrice !== '' &&
                                parseFloat(productUnitPrice) < parseFloat(minPrice)) {
                              return `נמוך ממחיר המינימום (₪${minPrice})`;
                            }
                            if (minPrice != null) {
                              return `מחיר מינימום: ₪${minPrice}`;
                            }
                            if (productUnitPrice !== (editingProductItem.OriginalPrice || editingProductItem.UnitPrice).toString()) {
                              return 'מחיר שונה מהמחיר המקורי';
                            }
                            return '';
                          })()}
                        />
                      </Box>
                      
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">
                          סה"כ:
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(parseFloat(productUnitPrice) * productQuantity)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelProductEdit}>ביטול</Button>
                <Button 
                  onClick={handleSaveProductEdit} 
                  variant="contained" 
                  color="primary"
                  disabled={
                    updateProductMutation.isLoading || 
                    // Only disable for inventory issues if storage is enabled
                    (storageEnabled && 
                    editingProductInventory && 
                    productQuantity > editingProductInventory.availableQuantity)
                  }
                >
                  {updateProductMutation.isLoading ? 'מעדכן...' : 'שמור שינויים'}
                </Button>
              </DialogActions>
            </MobileDialog>

            {/* Data-grid inventory adjust dialog — reused from ProductsPage
                so the Edit Product flow has an add/take UX consistent with
                the rest of the app. Role props mirror ProductsPage so a
                group that restricts Take to managers sees only Add, etc. */}
            {storageEnabled && (
              <InventoryManagementDialog
                open={adjustInventoryOpen}
                onClose={() => setAdjustInventoryOpen(false)}
                product={adjustInventoryProduct}
                storages={allStorages}
                initialStorageId={editingProductItem?.StorageID || null}
                onSuccess={handleInventoryAdjusted}
                canTakeInventory={canTakeInventory}
                canAddInventory={canAddInventory}
              />
            )}

            {/* Add Product Dialog (refactored single-page style) */}
            <AddProductsToOrderDialog
              open={addProductDialogOpen}
              onClose={() => setAddProductDialogOpen(false)}
              submitting={batchProductsMutation.isLoading || addProductMutation.isLoading}
              allProducts={products}
              allStorages={allStorages}
              storageEnabled={storageEnabled}
              barcodeEnabled={barcodeEnabled}
              productLabel={productLabel}
              productPluralLabel={productPluralLabel}
              productFields={productFields}
              productFieldsSchema={productFieldsSchema}
              canTakeInventory={canTakeInventory}
              canAddInventory={canAddInventory}
              onProductsRefetch={() => queryClient.invalidateQueries(["products"])}
              onSubmit={(addedItems) => {
                // Map the dialog's addedItems shape into the existing batch payload.
                const itemsToAdd = [];
                const itemsToUpdate = [];
                addedItems.forEach((newEntry) => {
                  const existingItem = order.items.find((item) => {
                    const sameProduct = String(item.ProductID) === String(newEntry.product.ProductID);
                    const samePrice = Math.abs(Number(item.UnitPrice) - Number(newEntry.customPrice || newEntry.product.Price)) < 0.001;
                    const sameStorage = storageEnabled ? item.StorageID === newEntry.storageId : true;
                    return sameProduct && samePrice && sameStorage;
                  });
                  if (existingItem) {
                    const updateData = {
                      OrderItemID: existingItem.OrderItemID,
                      Quantity: Math.round((parseFloat(existingItem.Quantity) + newEntry.quantity) * 100) / 100,
                      UnitPrice: Math.round(parseFloat(existingItem.UnitPrice) * 100) / 100,
                    };
                    if (storageEnabled && newEntry.storageId) updateData.StorageID = newEntry.storageId;
                    itemsToUpdate.push(updateData);
                  } else {
                    const addData = {
                      ProductID: newEntry.product.ProductID,
                      Quantity: Math.round(newEntry.quantity * 100) / 100,
                      UnitPrice: Math.round((newEntry.customPrice || newEntry.product.Price) * 100) / 100,
                    };
                    if (storageEnabled && newEntry.storageId) addData.StorageID = newEntry.storageId;
                    itemsToAdd.push(addData);
                  }
                });
                if (itemsToAdd.length > 0 || itemsToUpdate.length > 0) {
                  batchProductsMutation.mutate({ itemsToAdd, itemsToUpdate });
                }
              }}
            />
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default OrderDetailPage;
